<template>
  <div class="main-content">
    <!-- Header -->
    <div class="row mb-2">
      <div class="col-md-12">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="BookIcon"
            size="22"
            class="mr-1 text-primary"
          />
          <div>
            <h4 class="mb-0">
              {{ discipline.name || "Disciplina" }}
              <small class="text-muted font-weight-normal">
                — configuração para
                <feather-icon
                  icon="GitBranchIcon"
                  size="14"
                />
                {{ node.name || "Nó" }}
              </small>
            </h4>
            <small
              v-if="discipline.description && discipline.description !== discipline.name"
              class="text-muted"
            >{{ discipline.description }}</small>
          </div>
          <div class="ml-auto">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="14"
                class="mr-50"
              />
              Voltar
            </b-button>
          </div>
        </div>
        <hr>
      </div>
    </div>

    <!-- Abas de configuração da disciplina -->
    <b-tabs
      content-class="mt-2"
      nav-class="dc-tabs-nav"
    >
      <!-- ─── POSIÇÕES  ─────────────────────────────────────────── -->
      <b-tab
        title="Posições"
        active
      >
        <div class="row">
          <!-- Formulário de cadastro -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingPosition.id ? "Editar Posição" : "Nova Posição" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label><span class="required">*</span> Código:</label>
                  <input
                    v-model="editingPosition.code"
                    type="text"
                    class="form-control"
                    placeholder="Ex: A1, B2, 01"
                    maxlength="20"
                  >
                </div>
                <div class="form-group">
                  <label><span class="required">*</span> Nome:</label>
                  <input
                    v-model="editingPosition.name"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Saída da base"
                    maxlength="120"
                  >
                </div>
                <div class="form-group">
                  <label>Descrição:</label>
                  <textarea
                    v-model="editingPosition.description"
                    class="form-control"
                    rows="2"
                    placeholder="Descrição opcional..."
                  />
                </div>
                <div class="form-row">
                  <div class="form-group col-md-5">
                    <label>Ordem:</label>
                    <input
                      v-model="editingPosition.order"
                      type="number"
                      class="form-control"
                      min="0"
                    >
                  </div>
                  <div class="form-group col-md-7">
                    <label>Status:</label>
                    <select
                      v-model="editingPosition.status"
                      class="form-control"
                    >
                      <option :value="1">
                        Ativo
                      </option>
                      <option :value="0">
                        Inativo
                      </option>
                    </select>
                  </div>
                  <div class="form-group col-md-12">
                    <label>Escopo:</label>
                    <select
                      v-model="editingPosition.scope"
                      class="form-control"
                    >
                      <option value="ocorrencia">
                        Ocorrência &mdash; marco do atendimento
                      </option>
                      <option value="veiculo">
                        Veículo &mdash; estado da viatura
                      </option>
                    </select>
                    <small class="text-muted">
                      Veículo vale fora de qualquer ocorrência e n&atilde;o entra na linha do tempo do despacho.
                    </small>
                  </div>
                </div>

                <div class="form-group">
                  <b-form-checkbox
                    v-model="editingPosition.free_access"
                    switch
                  >
                    Acesso livre
                    <small class="text-muted d-block">
                      Pode ser informado a qualquer momento (ignora sucessão)
                    </small>
                  </b-form-checkbox>
                </div>
                <div class="form-group">
                  <b-form-checkbox
                    v-model="editingPosition.display_kanban"
                    switch
                  >
                    Exibir no Kanban
                  </b-form-checkbox>
                </div>
                <div class="form-group">
                  <b-form-checkbox
                    v-model="editingPosition.allows_dispatch"
                    switch
                  >
                    Permite despacho
                    <small class="text-muted d-block">
                      Equipes nesta posição podem ser despachadas
                    </small>
                  </b-form-checkbox>
                </div>
                <div class="form-group">
                  <b-form-checkbox
                    v-model="editingPosition.is_default_position"
                    switch
                  >
                    Posição padrão
                    <small class="text-muted d-block">
                      Equipes desta disciplina iniciam nesta posição ao serem criadas
                    </small>
                  </b-form-checkbox>
                </div>
                <div class="form-group">
                  <b-form-checkbox
                    v-model="editingPosition.releases_occurrence"
                    switch
                  >
                    Libera ocorrência
                    <small class="text-muted d-block">
                      Informar esta posição encerra a ocorrência associada
                    </small>
                  </b-form-checkbox>
                </div>

                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="savePosition"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingPosition.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingPosition.id"
                    variant="outline-secondary"
                    @click="cancelEdit"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de posições -->
          <div class="col-md-8">
            <div class="card">
              <div
                class="card-header d-flex align-items-center justify-content-between"
              >
                <span class="card-title mb-0">Posições configuradas</span>
                <small
                  class="text-muted"
                >{{ positions.length }} posição(ões)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingPositions"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!positions.length"
                  class="text-center text-muted py-4"
                >
                  Nenhuma posição cadastrada para esta disciplina.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th style="width: 36px" />
                      <th style="width: 60px">
                        Ordem
                      </th>
                      <th style="width: 80px">
                        Código
                      </th>
                      <th>Nome</th>
                      <th style="width: 150px">
                        N&oacute;
                      </th>
                      <th style="width: 110px">
                        Escopo
                      </th>
                      <th>Predecessores</th>
                      <th style="width: 90px">
                        Livre
                      </th>
                      <th style="width: 80px">
                        Despacho
                      </th>
                      <th style="width: 70px">
                        Padrão
                      </th>
                      <th style="width: 80px">
                        Lib. Ocor.
                      </th>
                      <th style="width: 80px">
                        Status
                      </th>
                      <th style="width: 100px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <draggable
                    v-model="orderedPositions"
                    tag="tbody"
                    handle=".drag-handle"
                    ghost-class="drag-ghost"
                    :disabled="!$hasAbility('node.admin')"
                    @end="onDragEnd"
                  >
                    <tr
                      v-for="pos in orderedPositions"
                      :key="pos.id"
                      :class="{ 'table-active': editingPosition.id === pos.id }"
                    >
                      <td
                        class="align-middle text-center drag-handle"
                        style="cursor: grab"
                      >
                        <feather-icon
                          icon="MenuIcon"
                          size="14"
                          class="text-muted"
                        />
                      </td>
                      <td class="align-middle text-center">
                        <span class="text-muted">{{ pos.order }}</span>
                      </td>
                      <td class="align-middle">
                        <b-badge variant="primary">
                          {{ pos.code }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ pos.name }}</span>
                        <br>
                        <small class="text-muted">{{ pos.description }}</small>
                      </td>
                      <td class="align-middle">
                        <span
                          v-if="pos.node"
                          class="small"
                          :class="daPropriaNo(pos) ? '' : 'text-muted'"
                          :title="textoDaOrigem(pos)"
                        >
                          {{ pos.node.name }}
                          <feather-icon
                            v-if="origemDaPosicao(pos) === 'inherited'"
                            icon="CornerRightDownIcon"
                            size="12"
                          />
                          <feather-icon
                            v-else-if="origemDaPosicao(pos) === 'descendant'"
                            icon="CornerRightUpIcon"
                            size="12"
                          />
                        </span>
                        <span
                          v-else
                          class="text-muted small"
                        >&mdash;</span>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="pos.scope === 'veiculo' ? 'light-warning' : 'light-info'">
                          {{ pos.scope === 'veiculo' ? 'Veículo' : 'Ocorrência' }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <span
                          v-if="pos.free_access"
                          class="text-success small"
                        >
                          <feather-icon
                            icon="UnlockIcon"
                            size="12"
                            class="mr-25"
                          />
                          Acesso livre
                        </span>
                        <template
                          v-else-if="
                            pos.predecessors && pos.predecessors.length
                          "
                        >
                          <b-badge
                            v-for="pred in pos.predecessors"
                            :key="pred.id"
                            variant="light-warning"
                            class="mr-25 mb-25"
                          >
                            {{ pred.code }}
                          </b-badge>
                        </template>
                        <span
                          v-else
                          class="text-muted small"
                        >—</span>
                        <!-- Botão configurar predecessores -->
                        <b-button
                          v-if="!pos.free_access && $hasAbility('node.admin')"
                          size="sm"
                          variant="flat-secondary"
                          class="p-0 ml-1"
                          title="Configurar predecessores"
                          @click="openPredecessors(pos)"
                        >
                          <feather-icon
                            icon="GitMergeIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                      <td class="align-middle text-center">
                        <b-form-checkbox
                          :checked="pos.free_access"
                          switch
                          size="sm"
                          :disabled="!$hasAbility('node.admin')"
                          @change="toggleFreeAccess(pos)"
                        />
                      </td>
                      <td class="align-middle text-center">
                        <b-form-checkbox
                          :checked="pos.allows_dispatch"
                          switch
                          size="sm"
                          :disabled="!$hasAbility('node.admin')"
                          @change="toggleAllowsDispatch(pos)"
                        />
                      </td>
                      <td class="align-middle text-center">
                        <feather-icon
                          v-if="pos.is_default_position"
                          icon="StarIcon"
                          size="15"
                          class="text-warning"
                          title="Posição padrão"
                        />
                        <span
                          v-else
                          class="text-muted"
                        >—</span>
                      </td>
                      <td class="align-middle text-center">
                        <b-form-checkbox
                          :checked="pos.releases_occurrence"
                          switch
                          size="sm"
                          :disabled="!$hasAbility('node.admin')"
                          @change="toggleReleasesOccurrence(pos)"
                        />
                      </td>
                      <td class="align-middle">
                        <b-badge
                          :variant="pos.status ? 'success' : 'secondary'"
                        >
                          {{ pos.status ? "Ativo" : "Inativo" }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          :disabled="!daPropriaNo(pos)"
                          :title="daPropriaNo(pos) ? 'Editar' : textoDaOrigem(pos)"
                          @click="editPosition(pos)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          v-if="$hasAbility('node.admin')"
                          size="sm"
                          variant="outline-danger"
                          :disabled="!daPropriaNo(pos)"
                          :title="daPropriaNo(pos) ? 'Excluir' : textoDaOrigem(pos)"
                          @click="deletePosition(pos)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </draggable>
                </table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── TIPOS E MOTIVOS ──────────────────────────────────────────── -->
      <b-tab title="Tipos e Motivos">
        <!-- == TIPOS == -->
        <h6 class="mb-1 text-primary">
          <feather-icon
            icon="TagIcon"
            size="15"
            class="mr-50"
          />
          Tipos de Ocorrência
        </h6>
        <div class="row">
          <!-- Formulário de tipo -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingType.id ? "Editar Tipo" : "Novo Tipo" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label><span class="required">*</span> Nome:</label>
                  <input
                    v-model="editingType.name"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Urgência"
                    maxlength="180"
                  >
                </div>
                <div class="form-group">
                  <label>Descrição:</label>
                  <textarea
                    v-model="editingType.description"
                    class="form-control"
                    rows="2"
                    placeholder="Descrição opcional..."
                  />
                </div>
                <div class="form-group">
                  <label>Status:</label>
                  <select
                    v-model="editingType.status"
                    class="form-control"
                  >
                    <option :value="1">
                      Ativo
                    </option>
                    <option :value="0">
                      Inativo
                    </option>
                  </select>
                </div>
                <div
                  v-if="editingType.id"
                  class="form-group"
                >
                  <label>Pin do Mapa:</label>
                  <div
                    class="d-flex align-items-center"
                    style="gap: 12px"
                  >
                    <div>
                      <img
                        v-if="editingType.pin_icon"
                        :src="editingType.pin_icon"
                        style="
                          width: 48px;
                          height: 48px;
                          object-fit: contain;
                          border: 1px solid #e0ddf5;
                          border-radius: 8px;
                        "
                      >
                      <div
                        v-else
                        class="pin-empty-preview"
                      >
                        Sem<br>pin
                      </div>
                    </div>
                    <div>
                      <label
                        v-if="$hasAbility('node.admin')"
                        class="btn btn-sm btn-outline-primary mb-1"
                        style="cursor: pointer"
                      >
                        <feather-icon
                          icon="UploadIcon"
                          size="13"
                          class="mr-25"
                        />
                        Carregar
                        <input
                          type="file"
                          accept="image/*"
                          style="display: none"
                          @change="uploadTypePin"
                        >
                      </label>
                      <br>
                      <a
                        v-if="editingType.pin_icon && $hasAbility('node.admin')"
                        href="#"
                        class="text-danger"
                        style="font-size: 0.78rem"
                        @click.prevent="removeTypePin"
                      >
                        Remover pin
                      </a>
                    </div>
                  </div>
                  <small
                    class="text-muted"
                  >Pin exibido no mapa para este tipo. Se não definido, usa o
                    pin da disciplina.</small>
                </div>
                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="saveType"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingType.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingType.id"
                    variant="outline-secondary"
                    @click="cancelEditType"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de tipos -->
          <div class="col-md-8">
            <div class="card">
              <div
                class="card-header d-flex align-items-center justify-content-between"
              >
                <span class="card-title mb-0">Tipos cadastrados</span>
                <small
                  class="text-muted"
                >{{ dispatchTypes.length }} tipo(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingTypes"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!dispatchTypes.length"
                  class="text-center text-muted py-4"
                >
                  Nenhum tipo cadastrado para esta disciplina.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th style="width: 100px">
                        Status
                      </th>
                      <th style="width: 100px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="t in dispatchTypes"
                      :key="t.id"
                      :class="{ 'table-active': editingType.id === t.id }"
                    >
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ t.name }}</span>
                        <small
                          v-if="t.description"
                          class="text-muted d-block"
                        >{{ t.description }}</small>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="t.status ? 'success' : 'secondary'">
                          {{ t.status ? "Ativo" : "Inativo" }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          title="Editar"
                          @click="editType(t)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          title="Excluir"
                          v-if="$hasAbility('node.admin')"
                          @click="deleteType(t)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <hr class="my-2">

        <!-- == MOTIVOS == -->
        <h6 class="mb-1 text-primary">
          <feather-icon
            icon="AlignLeftIcon"
            size="15"
            class="mr-50"
          />
          Motivos
        </h6>

        <!-- Seletor de tipo para filtrar motivos -->
        <div class="row mb-1">
          <div class="col-md-4">
            <div class="form-group mb-0">
              <label>Filtrar por Tipo:</label>
              <v-select
                v-model="selectedTypeIdForReasons"
                :options="dispatchTypes"
                :reduce="(t) => t.id"
                label="name"
                placeholder="Selecione um tipo..."
                class="vselect"
                @input="onReasonTypeChange"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Formulário de motivo -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingReason.id ? "Editar Motivo" : "Novo Motivo" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label>Tipo:</label>
                  <v-select
                    v-model="editingReason.dispatch_type_id"
                    :options="dispatchTypes"
                    :reduce="(t) => t.id"
                    label="name"
                    placeholder="Selecione o tipo..."
                    class="vselect"
                  />
                </div>
                <div class="form-group">
                  <label><span class="required">*</span> Nome:</label>
                  <input
                    v-model="editingReason.name"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Acidente de trânsito"
                    maxlength="180"
                  >
                </div>
                <div class="form-group">
                  <label>Descrição:</label>
                  <textarea
                    v-model="editingReason.description"
                    class="form-control"
                    rows="2"
                    placeholder="Descrição opcional..."
                  />
                </div>
                <div class="form-group">
                  <label>Status:</label>
                  <select
                    v-model="editingReason.status"
                    class="form-control"
                  >
                    <option :value="1">
                      Ativo
                    </option>
                    <option :value="0">
                      Inativo
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Interrogatório:</label>
                  <v-select
                    v-model="editingReason.interrogatory_id"
                    :options="interrogatories"
                    :reduce="(i) => i.id"
                    label="name"
                    placeholder="Nenhum (opcional)..."
                    class="vselect"
                    :clearable="true"
                  />
                  <small class="text-muted">Ao selecionar este motivo numa ocorrência, o interrogatório será exibido.</small>
                </div>
                <div
                  v-if="editingReason.interrogatory_id"
                  class="form-group"
                >
                  <div class="custom-control custom-switch">
                    <input
                      id="interr-reason-required-switch"
                      v-model="editingReason.interrogatory_required"
                      type="checkbox"
                      class="custom-control-input"
                    >
                    <label
                      class="custom-control-label"
                      for="interr-reason-required-switch"
                    >
                      Preenchimento obrigatório
                    </label>
                  </div>
                  <small class="text-muted">Se ativado, o operador não poderá registrar a ocorrência sem responder o interrogatório.</small>
                </div>
                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    :disabled="!editingReason.dispatch_type_id"
                    v-if="$hasAbility('node.admin')"
                    @click="saveReason"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingReason.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingReason.id"
                    variant="outline-secondary"
                    @click="cancelEditReason"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de motivos -->
          <div class="col-md-8">
            <div class="card">
              <div
                class="card-header d-flex align-items-center justify-content-between"
              >
                <span class="card-title mb-0">Motivos cadastrados</span>
                <small
                  class="text-muted"
                >{{ dispatchReasons.length }} motivo(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingReasons"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!filteredReasons.length"
                  class="text-center text-muted py-4"
                >
                  Nenhum motivo cadastrado{{
                    selectedTypeIdForReasons ? " para este tipo" : ""
                  }}.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th style="width: 100px">
                        Status
                      </th>
                      <th style="width: 100px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in filteredReasons"
                      :key="r.id"
                      :class="{ 'table-active': editingReason.id === r.id }"
                    >
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ r.name }}</span>
                        <small
                          v-if="r.description"
                          class="text-muted d-block"
                        >{{ r.description }}</small>
                      </td>
                      <td class="align-middle">
                        <small class="text-muted">{{
                          typeNameById(r.dispatch_type_id)
                        }}</small>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="r.status ? 'success' : 'secondary'">
                          {{ r.status ? "Ativo" : "Inativo" }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          title="Editar"
                          @click="editReason(r)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          title="Excluir"
                          v-if="$hasAbility('node.admin')"
                          @click="deleteReason(r)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── INTERROGATÓRIOS ───────────────────────────────────────────── -->
      <b-tab title="Interrogatórios">
        <div class="p-2">
          <!-- Seção: Interrogatórios -->
          <h6
            class="dc-section-title mb-1"
          >
            Interrogatórios
          </h6>
          <div class="row mb-3">
            <!-- Formulário -->
            <div class="col-md-4">
              <div class="card">
                <div class="card-header">
                  <span
                    class="card-title mb-0"
                  >{{
                    editingInterrogatory.id ? "Editar" : "Novo"
                  }}
                    Interrogatório</span>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label><span class="required">*</span> Nome:</label>
                    <input
                      v-model="editingInterrogatory.name"
                      type="text"
                      class="form-control"
                      placeholder="Ex: Triagem inicial"
                      maxlength="180"
                    >
                  </div>
                  <div class="form-group">
                    <label>Descrição:</label>
                    <textarea
                      v-model="editingInterrogatory.description"
                      class="form-control"
                      rows="2"
                      placeholder="Descrição opcional..."
                    />
                  </div>
                  <div class="form-group">
                    <label>Status:</label>
                    <select
                      v-model="editingInterrogatory.status"
                      class="form-control"
                    >
                      <option :value="1">
                        Ativo
                      </option>
                      <option :value="0">
                        Inativo
                      </option>
                    </select>
                  </div>
                  <div class="d-flex">
                    <b-button
                      variant="primary"
                      class="mr-1"
                      v-if="$hasAbility('node.admin')"
                      @click="saveInterrogatory"
                    >
                      <feather-icon
                        icon="SaveIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ editingInterrogatory.id ? "Atualizar" : "Adicionar" }}
                    </b-button>
                    <b-button
                      v-if="editingInterrogatory.id"
                      variant="outline-secondary"
                      @click="cancelEditInterrogatory"
                    >Cancelar</b-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabela de interrogatórios -->
            <div class="col-md-8">
              <div class="card">
                <div
                  class="card-header d-flex align-items-center justify-content-between"
                >
                  <span
                    class="card-title mb-0"
                  >Interrogatórios cadastrados</span>
                  <small
                    class="text-muted"
                  >{{ interrogatories.length }} registro(s)</small>
                </div>
                <div class="card-body p-0">
                  <div
                    v-if="loadingInterrogatories"
                    class="text-center py-4"
                  >
                    <b-spinner variant="primary" />
                  </div>
                  <div
                    v-else-if="!interrogatories.length"
                    class="text-center text-muted py-4"
                  >
                    Nenhum interrogatório cadastrado.
                  </div>
                  <table
                    v-else
                    class="table table-hover mb-0"
                  >
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th style="width: 80px">
                          Status
                        </th>
                        <th style="width: 120px">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in interrogatories"
                        :key="item.id"
                        :class="{
                          'table-active': selectedInterrogatoryId === item.id,
                        }"
                        style="cursor: pointer"
                        @click="selectInterrogatory(item)"
                      >
                        <td class="align-middle">
                          <span class="font-weight-bold">{{ item.name }}</span>
                          <small
                            v-if="item.description"
                            class="text-muted d-block"
                          >{{ item.description }}</small>
                        </td>
                        <td class="align-middle">
                          <b-badge
                            :variant="item.status ? 'success' : 'secondary'"
                          >{{ item.status ? "Ativo" : "Inativo" }}</b-badge>
                        </td>
                        <td class="align-middle">
                          <b-button
                            size="sm"
                            variant="outline-primary"
                            class="mr-50"
                            title="Editar"
                            @click.stop="editInterrogatory(item)"
                          >
                            <feather-icon
                              icon="EditIcon"
                              size="13"
                            />
                          </b-button>
                          <b-button
                            size="sm"
                            variant="outline-danger"
                            title="Excluir"
                            @click.stop="deleteInterrogatory(item)"
                          >
                            <feather-icon
                              icon="TrashIcon"
                              size="13"
                            />
                          </b-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Seção: Perguntas (aparece ao selecionar um interrogatório) -->
          <template v-if="selectedInterrogatoryId">
            <h6
              class="dc-section-title mb-1"
            >
              Perguntas do interrogatório selecionado
            </h6>
            <div class="row">
              <!-- Formulário pergunta -->
              <div :class="questionMode === 'edit' ? 'col-md-5' : 'col-md-4'">
                <div class="card">
                  <div class="card-header">
                    <span class="card-title mb-0">
                      {{ questionMode === "edit" ? "Editar Pergunta" : "Nova Pergunta" }}
                    </span>
                  </div>
                  <div class="card-body">
                    <!-- Campos sempre visíveis -->
                    <div class="form-group">
                      <label><span class="required">*</span> Pergunta:</label>
                      <textarea
                        v-model="editingQuestion.question"
                        class="form-control"
                        rows="3"
                        maxlength="500"
                        placeholder="Texto da pergunta..."
                      />
                    </div>
                    <div class="form-row">
                      <div class="form-group col-7">
                        <label>Tipo de resposta:</label>
                        <select
                          v-model.number="editingQuestion.question_type"
                          class="form-control"
                        >
                          <option :value="2">
                            Texto livre
                          </option>
                          <option :value="3">
                            Número
                          </option>
                          <option :value="4">
                            Alternativas
                          </option>
                        </select>
                      </div>
                      <div class="form-group col-5 d-flex flex-column justify-content-end pb-1">
                        <b-form-checkbox
                          v-model="editingQuestion.required"
                          switch
                        >
                          Obrigatória
                        </b-form-checkbox>
                      </div>
                    </div>

                    <!-- type=4: alternativas (disponível em ADD e EDIT) -->
                    <template v-if="editingQuestion.question_type === 4">
                      <div class="form-group">
                        <label>Alternativas:</label>
                        <div
                          v-for="(alt, idx) in editingAlternatives"
                          :key="idx"
                          class="card border mb-2 p-2"
                          style="background: #f9f9f9"
                        >
                          <div class="form-row mb-1">
                            <div class="form-group col-6 mb-0">
                              <label style="font-size:0.8rem">Rótulo:</label>
                              <input
                                v-model="alt.label"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Ex: Leve, Moderado, Grave..."
                              >
                            </div>
                            <div class="form-group col-6 mb-0">
                              <label style="font-size:0.8rem">Próxima pergunta:</label>
                              <select
                                v-model="alt.next_question_id"
                                class="form-control form-control-sm"
                              >
                                <option :value="null">
                                  Encerrar
                                </option>
                                <option
                                  v-for="q in questions.filter((q) => q.id !== editingQuestion.id)"
                                  :key="q.id"
                                  :value="q.id"
                                >
                                  {{ q.question.substring(0, 25) }}...
                                </option>
                              </select>
                            </div>
                          </div>
                          <div class="form-group mb-1">
                            <label style="font-size:0.8rem">Classificação desta alternativa:</label>
                            <v-select
                              v-model="alt.classification_id"
                              :options="classifications"
                              :reduce="(c) => c.id"
                              label="color_label"
                              placeholder="Nenhuma"
                              :clearable="true"
                              class="vselect"
                            >
                              <template #option="opt">
                                <span
                                  class="d-inline-block rounded-circle mr-1"
                                  style="width:12px;height:12px;vertical-align:middle"
                                  :style="{ background: opt.hex_color }"
                                />
                                {{ opt.color_label }}
                              </template>
                            </v-select>
                          </div>
                          <div class="form-group mb-1">
                            <label style="font-size:0.8rem">Tipos de Equipe desta alternativa:</label>
                            <div
                              v-for="(tt, ttIdx) in alt.team_types"
                              :key="ttIdx"
                              class="d-flex align-items-center mb-1"
                              style="gap: 6px"
                            >
                              <v-select
                                v-model="tt.team_type_id"
                                :options="teamTypes"
                                :reduce="(t) => t.id"
                                label="name"
                                placeholder="Selecione..."
                                class="vselect"
                                style="flex: 1"
                              />
                              <b-button
                                size="sm"
                                variant="outline-danger"
                                style="padding: 2px 7px; flex-shrink: 0"
                                @click="alt.team_types.splice(ttIdx, 1)"
                              >
                                <feather-icon
                                  icon="XIcon"
                                  size="12"
                                />
                              </b-button>
                            </div>
                            <b-button
                              size="sm"
                              variant="outline-primary"
                              @click="alt.team_types.push({ team_type_id: null })"
                            >
                              <feather-icon
                                icon="PlusIcon"
                                size="13"
                                class="mr-25"
                              />
                              Adicionar tipo
                            </b-button>
                          </div>
                          <div class="text-right">
                            <b-button
                              size="sm"
                              variant="outline-danger"
                              v-if="$hasAbility('node.admin')"
                              @click="removeAlternative(idx)"
                            >
                              <feather-icon
                                icon="TrashIcon"
                                size="12"
                              />
                              Remover alternativa
                            </b-button>
                          </div>
                        </div>
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mt-1"
                          @click="addAlternative"
                        >
                          <feather-icon
                            icon="PlusIcon"
                            size="13"
                            class="mr-25"
                          />
                          Adicionar alternativa
                        </b-button>
                      </div>
                    </template>

                    <!-- Campos extras apenas no modo EDITAR -->
                    <template v-if="questionMode === 'edit'">

                      <!-- type=2 or 3: próxima pergunta -->
                      <template v-if="editingQuestion.question_type === 2 || editingQuestion.question_type === 3">
                        <div class="form-group">
                          <label>Próxima pergunta:</label>
                          <select
                            v-model="editingQuestion.next_question_id"
                            class="form-control form-control-sm"
                          >
                            <option :value="null">
                              Encerrar
                            </option>
                            <option
                              v-for="q in questions.filter((q) => q.id !== editingQuestion.id)"
                              :key="q.id"
                              :value="q.id"
                            >
                              {{ q.question.substring(0, 50) }}{{ q.question.length > 50 ? "..." : "" }}
                            </option>
                          </select>
                        </div>
                      </template>

                      <div class="form-group">
                        <label>Status:</label>
                        <select
                          v-model="editingQuestion.status"
                          class="form-control form-control-sm"
                        >
                          <option :value="1">
                            Ativa
                          </option>
                          <option :value="0">
                            Inativa
                          </option>
                        </select>
                      </div>
                    </template>
                    <!-- Fim do modo EDITAR -->

                    <div class="d-flex mt-1">
                      <b-button
                        variant="primary"
                        class="mr-1"
                        v-if="$hasAbility('node.admin')"
                        @click="saveQuestion"
                      >
                        <feather-icon
                          icon="SaveIcon"
                          size="14"
                          class="mr-50"
                        />
                        {{ questionMode === "edit" ? "Atualizar" : "Adicionar" }}
                      </b-button>
                      <b-button
                        v-if="questionMode === 'edit'"
                        variant="outline-secondary"
                        @click="cancelEditQuestion"
                      >
                        Cancelar
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tabela de perguntas com drag-and-drop -->
              <div :class="questionMode === 'edit' ? 'col-md-7' : 'col-md-8'">
                <div class="card">
                  <div class="card-header d-flex align-items-center justify-content-between">
                    <span class="card-title mb-0">Perguntas cadastradas</span>
                    <small class="text-muted">{{ questions.length }} pergunta(s)</small>
                  </div>
                  <div class="card-body p-0">
                    <div
                      v-if="loadingQuestions"
                      class="text-center py-4"
                    >
                      <b-spinner variant="primary" />
                    </div>
                    <div
                      v-else-if="!questions.length"
                      class="text-center text-muted py-4"
                    >
                      Nenhuma pergunta cadastrada.
                    </div>
                    <table
                      v-else
                      class="table table-hover mb-0"
                      style="font-size: 0.85rem"
                    >
                      <thead>
                        <tr>
                          <th style="width: 28px" />
                          <th style="width: 36px">
                            #
                          </th>
                          <th>Pergunta</th>
                          <th style="width: 90px">
                            Tipo
                          </th>
                          <th style="width: 60px">
                            Obrig.
                          </th>
                          <th style="width: 80px">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <draggable
                        v-model="orderedQuestions"
                        tag="tbody"
                        handle=".drag-handle"
                        ghost-class="drag-ghost"
                        :disabled="!$hasAbility('node.admin')"
                        @end="onQuestionDragEnd"
                      >
                        <tr
                          v-for="q in orderedQuestions"
                          :key="q.id"
                          :class="{ 'table-active': editingQuestion.id === q.id }"
                        >
                          <td
                            class="align-middle text-center drag-handle"
                            style="cursor: grab"
                          >
                            <feather-icon
                              icon="MenuIcon"
                              size="14"
                              class="text-muted"
                            />
                          </td>
                          <td
                            class="align-middle text-center text-muted"
                            style="font-size:0.8rem"
                          >
                            {{ q.order }}
                          </td>
                          <td class="align-middle">
                            <span>{{ q.question.substring(0, 60) }}{{ q.question.length > 60 ? "..." : "" }}</span>
                            <div
                              v-if="q.question_type === 2 || q.question_type === 3"
                              class="mt-1"
                              style="font-size: 0.78rem; color: #888"
                            >
                              → {{ questionNameById(q.next_question_id) }}
                            </div>
                            <div
                              v-else-if="q.question_type === 4 && q.alternatives && q.alternatives.length"
                              class="mt-1"
                              style="font-size: 0.78rem; color: #888"
                            >
                              <span
                                v-for="(alt, i) in q.alternatives"
                                :key="i"
                                class="mr-1"
                              >
                                "{{ alt.label }}" → {{ questionNameById(alt.next_question_id)
                                }}<span v-if="i < q.alternatives.length - 1">;</span>
                              </span>
                            </div>
                          </td>
                          <td class="align-middle text-center">
                            <b-badge
                              :variant="['', 'secondary', 'info', 'warning', 'success'][q.question_type || 2]"
                              style="font-size: 0.72rem"
                            >
                              {{ ["", "", "Texto", "Número", "Alts."][q.question_type || 2] }}
                            </b-badge>
                          </td>
                          <td class="align-middle text-center">
                            <b-badge :variant="q.required ? 'danger' : 'secondary'">
                              {{ q.required ? "Sim" : "Não" }}
                            </b-badge>
                          </td>
                          <td class="align-middle">
                            <b-button
                              size="sm"
                              variant="outline-primary"
                              class="mr-50"
                              title="Editar"
                              @click="editQuestion(q)"
                            >
                              <feather-icon
                                icon="EditIcon"
                                size="13"
                              />
                            </b-button>
                            <b-button
                              size="sm"
                              variant="outline-danger"
                              title="Excluir"
                              v-if="$hasAbility('node.admin')"
                              @click="deleteQuestion(q)"
                            >
                              <feather-icon
                                icon="TrashIcon"
                                size="13"
                              />
                            </b-button>
                          </td>
                        </tr>
                      </draggable>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </b-tab>

      <!-- ─── DESFECHOS ─────────────────────────────────────────────────── -->
      <b-tab title="Desfechos">
        <div class="row">
          <!-- Formulário -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingOutcome.id ? "Editar Desfecho" : "Novo Desfecho" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label><span class="required">*</span> Nome:</label>
                  <input
                    v-model="editingOutcome.name"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Alta hospitalar"
                    maxlength="180"
                  >
                </div>
                <div class="form-group">
                  <label>Descrição:</label>
                  <textarea
                    v-model="editingOutcome.description"
                    class="form-control"
                    rows="2"
                    placeholder="Descrição opcional..."
                  />
                </div>
                <div class="form-group">
                  <label>Contextos de uso:</label>
                  <v-select
                    v-model="editingOutcome.types"
                    :options="outcomeTypes"
                    :reduce="(ot) => ot.id"
                    label="name_outcome"
                    multiple
                    placeholder="Selecione os contextos..."
                    class="vselect"
                  />
                  <small class="text-muted">
                    Define em quais situações este desfecho pode ser usado (Ocorrencia, Envolvido, Mobile).
                  </small>
                </div>
                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="saveOutcome"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingOutcome.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingOutcome.id"
                    variant="outline-secondary"
                    @click="cancelEditOutcome"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista -->
          <div class="col-md-8">
            <div class="card">
              <div
                class="card-header d-flex align-items-center justify-content-between"
              >
                <span class="card-title mb-0">Desfechos cadastrados</span>
                <small
                  class="text-muted"
                >{{ outcomes.length }} desfecho(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingOutcomes"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!outcomes.length"
                  class="text-center text-muted py-4"
                >
                  Nenhum desfecho cadastrado para esta disciplina.
                </div>
                <div
                  v-else
                  class="table-responsive"
                >
                  <table class="table table-sm table-hover mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>Nome</th>
                        <th>Contextos</th>
                        <th style="width: 90px" />
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="oc in outcomes"
                        :key="oc.id"
                        :class="{
                          'table-active': editingOutcome.id === oc.id,
                        }"
                      >
                        <td>{{ oc.name }}</td>
                        <td>
                          <span
                            v-for="t in oc.types"
                            :key="t.id"
                            class="badge badge-secondary mr-1"
                          >{{ t.name_outcome }}</span>
                          <span
                            v-if="!oc.types || !oc.types.length"
                            class="text-muted"
                          >—</span>
                        </td>
                        <td class="text-right">
                          <b-button
                            size="sm"
                            variant="outline-primary"
                            class="mr-1"
                            @click="editOutcome(oc)"
                          >
                            <feather-icon
                              icon="EditIcon"
                              size="12"
                            />
                          </b-button>
                          <b-button
                            size="sm"
                            variant="outline-danger"
                            v-if="$hasAbility('node.admin')"
                            @click="deleteOutcome(oc)"
                          >
                            <feather-icon
                              icon="TrashIcon"
                              size="12"
                            />
                          </b-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── CLASSIFICAÇÕES ────────────────────────────────────────────── -->
      <b-tab title="Classificações">
        <div class="row">
          <!-- Formulário -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingClassification.id ? "Editar Classificação" : "Nova Classificação" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-row">
                  <div class="form-group col-md-5">
                    <label><span class="required">*</span> Cor (HEX):</label>
                    <div
                      class="d-flex align-items-center"
                      style="gap: 6px"
                    >
                      <input
                        v-model="editingClassification.hex_color"
                        type="color"
                        class="form-control p-0"
                        style="width: 42px; height: 38px; cursor: pointer; flex-shrink: 0"
                      >
                      <input
                        v-model="editingClassification.hex_color"
                        type="text"
                        class="form-control"
                        placeholder="#FF0000"
                        maxlength="7"
                        style="font-family: monospace"
                      >
                    </div>
                  </div>
                  <div class="form-group col-md-7">
                    <label><span class="required">*</span> Rótulo:</label>
                    <input
                      v-model="editingClassification.color_label"
                      type="text"
                      class="form-control"
                      placeholder="Ex: Vermelho, Laranja"
                      maxlength="50"
                    >
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label><span class="required">*</span> Peso:</label>
                    <input
                      v-model.number="editingClassification.weight"
                      type="number"
                      class="form-control"
                      min="0"
                      placeholder="Ex: 10"
                    >
                    <small class="text-muted">Maior peso = mais crítico</small>
                  </div>
                  <div class="form-group col-md-6">
                    <label>Status:</label>
                    <select
                      v-model="editingClassification.status"
                      class="form-control"
                    >
                      <option :value="1">
                        Ativo
                      </option>
                      <option :value="0">
                        Inativo
                      </option>
                    </select>
                  </div>
                </div>

                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="saveClassification"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingClassification.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingClassification.id"
                    variant="outline-secondary"
                    @click="cancelEditClassification"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista -->
          <div class="col-md-8">
            <div class="card">
              <div class="card-header d-flex align-items-center justify-content-between">
                <span class="card-title mb-0">Classificações cadastradas</span>
                <small class="text-muted">{{ classifications.length }} registro(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingClassifications"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!classifications.length"
                  class="text-center text-muted py-4"
                >
                  Nenhuma classificação cadastrada para esta disciplina.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th>Rótulo</th>
                      <th style="width: 90px">
                        Cor
                      </th>
                      <th style="width: 70px">
                        Peso
                      </th>
                      <th style="width: 80px">
                        Status
                      </th>
                      <th style="width: 100px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="c in classifications"
                      :key="c.id"
                      :class="{ 'table-active': editingClassification.id === c.id }"
                    >
                      <td class="align-middle">
                        <div
                          class="d-flex align-items-center"
                          style="gap: 8px"
                        >
                          <div
                            :style="{ backgroundColor: c.hex_color }"
                            style="width: 14px; height: 14px; border-radius: 3px; border: 1px solid rgba(0,0,0,.15); flex-shrink: 0"
                          />
                          <span class="font-weight-bold">{{ c.color_label }}</span>
                          <code
                            class="text-muted"
                            style="font-size: 0.75rem"
                          >{{ c.hex_color }}</code>
                        </div>
                      </td>
                      <td class="align-middle">
                        <div
                          :style="{ backgroundColor: c.hex_color }"
                          style="width: 100%; height: 24px; border-radius: 4px; border: 1px solid rgba(0,0,0,.1)"
                        />
                      </td>
                      <td class="align-middle text-center">
                        <b-badge variant="light-secondary">
                          {{ c.weight }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="c.status ? 'success' : 'secondary'">
                          {{ c.status ? "Ativo" : "Inativo" }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          title="Editar"
                          @click="editClassification(c)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          title="Excluir"
                          v-if="$hasAbility('node.admin')"
                          @click="deleteClassification(c)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── INTERCORRÊNCIAS ───────────────────────────────────────────── -->
      <b-tab title="Intercorrências">
        <div class="row">
          <!-- Formulário -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingIntercurrence.id ? "Editar Intercorrência" : "Nova Intercorrência" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label><span class="required">*</span> Nome:</label>
                  <input
                    v-model="editingIntercurrence.name"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Parada cardiorrespiratória"
                    maxlength="150"
                  >
                </div>
                <div class="form-group">
                  <label>Descrição:</label>
                  <textarea
                    v-model="editingIntercurrence.description"
                    class="form-control"
                    rows="2"
                    placeholder="Descrição opcional..."
                  />
                </div>
                <div class="form-group">
                  <label>Status:</label>
                  <select
                    v-model="editingIntercurrence.is_active"
                    class="form-control"
                  >
                    <option :value="true">
                      Ativo
                    </option>
                    <option :value="false">
                      Inativo
                    </option>
                  </select>
                </div>
                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="saveIntercurrence"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingIntercurrence.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingIntercurrence.id"
                    variant="outline-secondary"
                    @click="cancelEditIntercurrence"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista -->
          <div class="col-md-8">
            <div class="card">
              <div class="card-header d-flex align-items-center justify-content-between">
                <span class="card-title mb-0">Intercorrências cadastradas</span>
                <small class="text-muted">{{ intercurrences.length }} registro(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingIntercurrences"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!intercurrences.length"
                  class="text-center text-muted py-4"
                >
                  Nenhuma intercorrência cadastrada para esta disciplina.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th style="width: 90px">
                        Status
                      </th>
                      <th style="width: 100px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in intercurrences"
                      :key="item.id"
                      :class="{ 'table-active': editingIntercurrence.id === item.id }"
                    >
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ item.name }}</span>
                        <small
                          v-if="item.description"
                          class="text-muted d-block"
                        >{{ item.description }}</small>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="item.is_active ? 'success' : 'secondary'">
                          {{ item.is_active ? "Ativo" : "Inativo" }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          title="Editar"
                          @click="editIntercurrence(item)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          title="Excluir"
                          v-if="$hasAbility('node.admin')"
                          @click="deleteIntercurrence(item)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── AVALIAÇÃO INICIAL ──────────────────────────────────────── -->
      <b-tab title="Avaliação Inicial">
        <div class="row">
          <!-- Formulário de pergunta -->
          <div class="col-md-5">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingAssessmentQuestion.id ? "Editar Pergunta" : "Nova Pergunta" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label><span class="required">*</span> Rótulo / Pergunta:</label>
                  <input
                    v-model="editingAssessmentQuestion.label"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Ausculta"
                    maxlength="200"
                  >
                </div>
                <div class="form-group">
                  <label><span class="required">*</span> Tipo:</label>
                  <select
                    v-model="editingAssessmentQuestion.type"
                    class="form-control"
                  >
                    <option value="single_select">
                      Seleção Única
                    </option>
                    <option value="multiple_select">
                      Seleção Múltipla
                    </option>
                    <option value="text">
                      Texto Livre
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Ordem:</label>
                  <input
                    v-model.number="editingAssessmentQuestion.order"
                    type="number"
                    class="form-control"
                    min="0"
                    placeholder="0"
                    style="width: 100px"
                  >
                </div>
                <div class="form-group">
                  <label>Status:</label>
                  <select
                    v-model="editingAssessmentQuestion.is_active"
                    class="form-control"
                  >
                    <option :value="true">
                      Ativo
                    </option>
                    <option :value="false">
                      Inativo
                    </option>
                  </select>
                </div>

                <!-- Opções de resposta (para tipos de seleção) -->
                <div
                  v-if="editingAssessmentQuestion.type === 'single_select' || editingAssessmentQuestion.type === 'multiple_select'"
                  class="form-group"
                >
                  <label>Opções de resposta:</label>
                  <div
                    class="d-flex mb-1"
                    style="gap: 6px"
                  >
                    <input
                      v-model="inlineOptionInput"
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Ex: Normal"
                      maxlength="150"
                      @keyup.enter="addInlineOption"
                    >
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      :disabled="!inlineOptionInput.trim()"
                      @click="addInlineOption"
                    >
                      <feather-icon
                        icon="PlusIcon"
                        size="13"
                      />
                    </b-button>
                  </div>
                  <div
                    v-if="!editingAssessmentQuestionOptions.length"
                    class="text-muted"
                    style="font-size: 12px"
                  >
                    Nenhuma opção adicionada.
                  </div>
                  <div
                    v-else
                    class="d-flex flex-wrap"
                    style="gap: 4px"
                  >
                    <span
                      v-for="(opt, idx) in editingAssessmentQuestionOptions"
                      :key="idx"
                      class="badge badge-light-primary d-inline-flex align-items-center"
                      style="font-size: 12px; gap: 4px; padding: 4px 8px"
                    >
                      {{ opt.label }}
                      <button
                        class="btn btn-link p-0 text-danger"
                        style="font-size: 11px; line-height: 1"
                        title="Remover"
                        v-if="$hasAbility('node.admin')"
                        @click="removeInlineOption(opt, idx)"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="11"
                        />
                      </button>
                    </span>
                  </div>
                </div>

                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="saveAssessmentQuestion"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingAssessmentQuestion.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingAssessmentQuestion.id"
                    variant="outline-secondary"
                    @click="cancelEditAssessmentQuestion"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de perguntas -->
          <div class="col-md-7">
            <div class="card">
              <div class="card-header d-flex align-items-center justify-content-between">
                <span class="card-title mb-0">Perguntas cadastradas</span>
                <small class="text-muted">{{ assessmentQuestions.length }} pergunta(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingAssessmentQuestions"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!assessmentQuestions.length"
                  class="text-center text-muted py-4"
                >
                  Nenhuma pergunta cadastrada para esta disciplina.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th style="width: 36px" />
                      <th style="width: 60px">
                        Ordem
                      </th>
                      <th>Rótulo</th>
                      <th style="width: 120px">
                        Tipo
                      </th>
                      <th style="width: 80px">
                        Status
                      </th>
                      <th style="width: 90px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <draggable
                    v-model="orderedAssessmentQuestions"
                    tag="tbody"
                    handle=".drag-handle"
                    ghost-class="drag-ghost"
                    :disabled="!$hasAbility('node.admin')"
                    @end="onAssessmentQuestionDragEnd"
                  >
                    <tr
                      v-for="q in orderedAssessmentQuestions"
                      :key="q.id"
                      :class="{ 'table-active': editingAssessmentQuestion.id === q.id }"
                    >
                      <td
                        class="align-middle text-center drag-handle"
                        style="cursor: grab"
                      >
                        <feather-icon
                          icon="MenuIcon"
                          size="14"
                          class="text-muted"
                        />
                      </td>
                      <td class="align-middle text-center">
                        <span class="text-muted">{{ q.order }}</span>
                      </td>
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ q.label }}</span>
                        <div
                          v-if="q.type !== 'text' && q.options && q.options.length"
                          class="d-flex flex-wrap mt-25"
                          style="gap: 3px"
                        >
                          <span
                            v-for="opt in q.options"
                            :key="opt.id"
                            class="badge badge-light-secondary"
                            style="font-size: 10px"
                          >{{ opt.label }}</span>
                        </div>
                        <div v-else-if="q.type !== 'text'">
                          <small class="text-muted">Sem opções. Clique em editar.</small>
                        </div>
                      </td>
                      <td class="align-middle">
                        <b-badge
                          :variant="typeVariant(q.type)"
                          style="font-size: 11px"
                        >
                          {{ typeLabel(q.type) }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="q.is_active ? 'success' : 'secondary'">
                          {{ q.is_active ? 'Ativo' : 'Inativo' }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          title="Editar"
                          @click="beginEditAssessmentQuestion(q)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          title="Excluir"
                          v-if="$hasAbility('node.admin')"
                          @click="deleteAssessmentQuestion(q)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </draggable>
                </table>
              </div>
            </div>
          </div>
        </div>

      </b-tab>

      <!-- ─── PROBLEMAS ──────────────────────────────────────────────── -->
      <b-tab title="Problemas">
        <div class="row">
          <!-- Formulário de problema -->
          <div class="col-md-5">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingProblem.id ? "Editar Problema" : "Novo Problema" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label><span class="required">*</span> Problema / Pergunta:</label>
                  <input
                    v-model="editingProblem.label"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Possui alergia conhecida?"
                    maxlength="200"
                  >
                  <small class="text-muted">A resposta no atendimento será Sim ou Não.</small>
                </div>
                <div class="form-group">
                  <label>Ordem:</label>
                  <input
                    v-model.number="editingProblem.order"
                    type="number"
                    class="form-control"
                    min="0"
                    placeholder="0"
                    style="width: 100px"
                  >
                </div>
                <div class="form-group">
                  <label>Status:</label>
                  <select
                    v-model="editingProblem.is_active"
                    class="form-control"
                  >
                    <option :value="true">
                      Ativo
                    </option>
                    <option :value="false">
                      Inativo
                    </option>
                  </select>
                </div>
                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="saveProblem"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingProblem.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingProblem.id"
                    variant="outline-secondary"
                    @click="cancelEditProblem"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de problemas -->
          <div class="col-md-7">
            <div class="card">
              <div class="card-header d-flex align-items-center justify-content-between">
                <span class="card-title mb-0">Problemas cadastrados</span>
                <small class="text-muted">{{ problems.length }} problema(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingProblems"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!problems.length"
                  class="text-center text-muted py-4"
                >
                  Nenhum problema cadastrado para esta disciplina.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th style="width: 36px" />
                      <th style="width: 60px">
                        Ordem
                      </th>
                      <th>Problema</th>
                      <th style="width: 80px">
                        Status
                      </th>
                      <th style="width: 90px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <draggable
                    v-model="orderedProblems"
                    tag="tbody"
                    handle=".drag-handle"
                    ghost-class="drag-ghost"
                    :disabled="!$hasAbility('node.admin')"
                    @end="onProblemDragEnd"
                  >
                    <tr
                      v-for="p in orderedProblems"
                      :key="p.id"
                      :class="{ 'table-active': editingProblem.id === p.id }"
                    >
                      <td
                        class="align-middle text-center drag-handle"
                        style="cursor: grab"
                      >
                        <feather-icon
                          icon="MenuIcon"
                          size="14"
                          class="text-muted"
                        />
                      </td>
                      <td class="align-middle text-center">
                        <span class="text-muted">{{ p.order }}</span>
                      </td>
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ p.label }}</span>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="p.is_active ? 'success' : 'secondary'">
                          {{ p.is_active ? 'Ativo' : 'Inativo' }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          title="Editar"
                          @click="beginEditProblem(p)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          title="Excluir"
                          v-if="$hasAbility('node.admin')"
                          @click="deleteProblem(p)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </draggable>
                </table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── FLUXO DE OCORRÊNCIA ──────────────────────────────────────── -->
      <b-tab title="Fluxo de Ocorrência">
        <div class="row mt-1">
          <div class="col-md-8">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">Pipeline de Etapas</span>
              </div>
              <div class="card-body pb-2">
                <p class="text-muted small mb-2">
                  Etapas intermediárias configuradas via seed. Ative/desative e
                  configure quando cada etapa pode ser pulada.
                </p>

                <!-- Etapa fixa: SOLICITA -->
                <div class="flow-step-fixed">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="mr-50 text-primary"
                  />
                  <strong>SOLICITA</strong>
                  <span
                    class="text-muted ml-1 small"
                  >(TARM — fixo, início)</span>
                </div>
                <div class="flow-step-arrow">
                  ↓
                </div>

                <!-- Etapas intermediárias -->
                <div
                  v-if="flowSteps.length === 0"
                  class="text-center text-muted small py-2"
                  style="border: 1px dashed #ddd; border-radius: 6px"
                >
                  Nenhuma etapa intermediária — a ocorrência vai direto para
                  despacho.
                </div>

                <div
                  v-for="step in flowSteps"
                  :key="step.id"
                >
                  <div
                    class="flow-step-row"
                    :class="{ 'flow-step-inactive': !step.status }"
                  >
                    <span
                      class="badge mr-1"
                      :class="
                        step.status
                          ? 'badge-light-success'
                          : 'badge-light-secondary'
                      "
                    >{{ step.status ? "Ativo" : "Inativo" }}</span>

                    <div class="flex-grow-1">
                      <strong>{{ step.step_label }}</strong>
                      <code
                        class="ml-1 text-muted"
                        style="font-size: 0.75rem"
                      >{{ step.step_key }}</code>
                      <div
                        v-if="
                          step.skip_conditions && step.skip_conditions.length
                        "
                        class="small text-muted mt-25"
                      >
                        Pulado por: {{ skipSummary(step) }}
                      </div>
                    </div>

                    <div class="d-flex align-items-center">
                      <b-button
                        size="sm"
                        :variant="
                          step.status ? 'outline-warning' : 'outline-success'
                        "
                        class="mr-50"
                        v-if="$hasAbility('node.admin')"
                        @click="toggleFlowStep(step)"
                      >
                        {{ step.status ? "Desativar" : "Ativar" }}
                      </b-button>
                      <b-button
                        v-if="$hasAbility('node.admin')"
                        size="sm"
                        variant="flat-primary"
                        @click="openSkipModal(step)"
                      >
                        <feather-icon
                          icon="SkipForwardIcon"
                          size="12"
                          class="mr-25"
                        />
                        Pulos
                      </b-button>
                    </div>
                  </div>
                  <div class="flow-step-arrow">
                    ↓
                  </div>
                </div>

                <!-- Etapa fixa: DESPACHA -->
                <div class="flow-step-fixed flow-step-fixed--end">
                  <feather-icon
                    icon="RadioIcon"
                    size="14"
                    class="mr-50 text-success"
                  />
                  <strong>DESPACHA</strong>
                  <span
                    class="text-muted ml-1 small"
                  >(Rádio Operador — fixo, fim)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
      <!-- ===== Decisões Técnicas ===== -->
      <b-tab title="Decisões Técnicas">
        <div class="row">
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingTechnicalDecision.id ? "Editar Decisão" : "Nova Decisão Técnica" }}
                </span>
              </div>
              <div
                v-if="!editingTechnicalDecision.is_fixed"
                class="card-body"
              >
                <div class="form-group">
                  <label><span class="required">*</span> Nome:</label>
                  <input
                    v-model="editingTechnicalDecision.name"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Regulação médica"
                    maxlength="180"
                  >
                </div>
                <div class="form-group">
                  <label>Status:</label>
                  <select
                    v-model="editingTechnicalDecision.status"
                    class="form-control"
                  >
                    <option :value="1">
                      Ativo
                    </option>
                    <option :value="0">
                      Inativo
                    </option>
                  </select>
                </div>
                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('node.admin')"
                    @click="saveTechnicalDecision"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingTechnicalDecision.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingTechnicalDecision.id"
                    variant="outline-secondary"
                    @click="cancelEditTechnicalDecision"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
              <div
                v-else
                class="card-body text-muted small"
              >
                Decisões do sistema não podem ser editadas. Apenas o status pode ser alterado.
              </div>
            </div>
          </div>

          <div class="col-md-8">
            <div class="card">
              <div class="card-header d-flex align-items-center justify-content-between">
                <span class="card-title mb-0">Decisões cadastradas</span>
                <small class="text-muted">{{ technicalDecisions.length }} registro(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingTechnicalDecisions"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!technicalDecisions.length"
                  class="text-center text-muted py-4"
                >
                  Nenhuma decisão técnica cadastrada.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th style="width: 90px">
                        Tipo
                      </th>
                      <th style="width: 90px">
                        Status
                      </th>
                      <th style="width: 150px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in technicalDecisions"
                      :key="item.id"
                      :class="{ 'table-active': editingTechnicalDecision.id === item.id }"
                    >
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ item.name }}</span>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="item.is_fixed ? 'secondary' : 'info'">
                          {{ item.is_fixed ? "Sistema" : "Custom" }}
                        </b-badge>
                      </td>
                      <td class="align-middle">
                        <b-badge :variant="item.status ? 'success' : 'secondary'">
                          {{ item.status ? "Ativo" : "Inativo" }}
                        </b-badge>
                      </td>
                      <td
                        class="align-middle"
                        style="white-space: nowrap"
                      >
                        <b-button
                          size="sm"
                          :variant="item.status ? 'outline-warning' : 'outline-success'"
                          class="mr-50"
                          :title="item.status ? 'Inativar' : 'Ativar'"
                          v-if="$hasAbility('node.admin')"
                          @click="toggleTechnicalDecisionStatus(item)"
                        >
                          <feather-icon
                            :icon="item.status ? 'EyeOffIcon' : 'EyeIcon'"
                            size="13"
                          />
                        </b-button>
                        <template v-if="!item.is_fixed">
                          <b-button
                            size="sm"
                            variant="outline-primary"
                            class="mr-50"
                            title="Editar"
                            @click="editTechnicalDecision(item)"
                          >
                            <feather-icon
                              icon="EditIcon"
                              size="13"
                            />
                          </b-button>
                          <b-button
                            size="sm"
                            variant="outline-danger"
                            title="Excluir"
                            v-if="$hasAbility('node.admin')"
                            @click="deleteTechnicalDecision(item)"
                          >
                            <feather-icon
                              icon="TrashIcon"
                              size="13"
                            />
                          </b-button>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── OUTRAS CONFIGURAÇÕES (sempre por último) ─────────────────── -->
      <b-tab title="Outras configurações">
        <div class="p-2">
          <h6
            class="dc-section-title mb-2"
          >
            Pin da Disciplina
          </h6>
          <div
            class="card"
            style="max-width: 420px"
          >
            <div class="card-body">
              <p class="text-muted small mb-2">
                Este pin é exibido no mapa para todas as ocorrências desta
                disciplina que não tenham um pin personalizado no tipo.
              </p>
              <div
                class="d-flex align-items-center"
                style="gap: 16px"
              >
                <div>
                  <img
                    v-if="discipline.pin_icon"
                    :src="discipline.pin_icon"
                    style="
                      width: 64px;
                      height: 64px;
                      object-fit: contain;
                      border: 1px solid #ddd;
                      border-radius: 8px;
                    "
                  >
                  <div
                    v-else
                    class="pin-empty-preview"
                    style="width: 64px; height: 64px; font-size: 11px"
                  >
                    Sem<br>pin
                  </div>
                </div>
                <div>
                  <label
                    class="btn btn-sm btn-outline-primary mb-1"
                    style="cursor: pointer"
                  >
                    <feather-icon
                      icon="UploadIcon"
                      size="13"
                      class="mr-25"
                    />
                    Carregar pin
                    <input
                      type="file"
                      accept="image/*"
                      style="display: none"
                      @change="uploadDisciplinePin"
                    >
                  </label>
                  <br>
                  <a
                    v-if="discipline.pin_icon"
                    href="#"
                    class="text-danger"
                    style="font-size: 0.78rem"
                    @click.prevent="removeDisciplinePin"
                  >Remover pin</a>
                </div>
              </div>
            </div>
          </div>

          <hr />

          <h6
            class="dc-section-title mb-2"
          >
            Naturezas de envolvido
          </h6>
          <div class="card" style="max-width: 520px">
            <div class="card-body">
              <p class="text-muted small mb-2">
                Quais naturezas ficam disponíveis nesta disciplina. Use a
                engrenagem para configurar os papéis de cada natureza. Sem
                nenhuma marcada, todas ficam disponíveis.
              </p>
              <div v-if="loadingEntityTypes" class="text-muted small">
                <b-spinner small /> Carregando...
              </div>
              <div v-else>
                <div
                  v-for="t in disciplineEntityTypes"
                  :key="t.id"
                  class="d-flex align-items-center justify-content-between mb-2"
                  style="max-width: 360px"
                >
                  <div class="custom-control custom-switch">
                    <input
                      :id="`det-${t.id}`"
                      v-model="t.enabled"
                      type="checkbox"
                      class="custom-control-input"
                    >
                    <label class="custom-control-label" :for="`det-${t.id}`">
                      {{ t.label }}
                    </label>
                  </div>
                  <button
                    v-if="$hasAbility('node.admin')"
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    title="Configurar papéis desta natureza"
                    @click="openRoleModal(t)"
                  >
                    <feather-icon icon="SettingsIcon" size="14" />
                  </button>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-primary mt-1"
                  :disabled="savingEntityTypes"
                  v-if="$hasAbility('node.admin')"
                  @click="saveDisciplineEntityTypes"
                >
                  <b-spinner v-if="savingEntityTypes" small class="mr-25" />
                  Salvar naturezas
                </button>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ─── FORMULÁRIOS ──────────────────────────────────────────────── -->
      <b-tab title="Formulários">
        <div class="row">
          <!-- Formulário de cadastro (Seção A: definições) -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header">
                <span class="card-title mb-0">
                  {{ editingFormDefinition.id ? "Editar Formulário" : "Novo Formulário" }}
                </span>
              </div>
              <div class="card-body">
                <div class="form-group">
                  <label><span class="required">*</span> Nome:</label>
                  <input
                    v-model="editingFormDefinition.name"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Avaliação de trauma"
                    maxlength="180"
                  >
                </div>
                <div class="form-group">
                  <label>Descrição:</label>
                  <textarea
                    v-model="editingFormDefinition.description"
                    class="form-control"
                    rows="2"
                    placeholder="Descrição opcional..."
                  />
                </div>
                <div class="form-group">
                  <label>Status:</label>
                  <select
                    v-model="editingFormDefinition.status"
                    class="form-control"
                  >
                    <option :value="true">
                      Publicado
                    </option>
                    <option :value="false">
                      Rascunho
                    </option>
                  </select>
                </div>
                <div class="d-flex">
                  <b-button
                    variant="primary"
                    class="mr-1"
                    v-if="$hasAbility('forms.admin')"
                    @click="saveFormDefinition"
                  >
                    <feather-icon
                      icon="SaveIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ editingFormDefinition.id ? "Atualizar" : "Adicionar" }}
                  </b-button>
                  <b-button
                    v-if="editingFormDefinition.id"
                    variant="outline-secondary"
                    @click="cancelEditFormDefinition"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista de formulários -->
          <div class="col-md-8">
            <div class="card">
              <div
                class="card-header d-flex align-items-center justify-content-between"
              >
                <span class="card-title mb-0">Formulários cadastrados</span>
                <small
                  class="text-muted"
                >{{ formDefinitions.length }} formulário(s)</small>
              </div>
              <div class="card-body p-0">
                <div
                  v-if="loadingFormDefinitions"
                  class="text-center py-4"
                >
                  <b-spinner variant="primary" />
                </div>
                <div
                  v-else-if="!formDefinitions.length"
                  class="text-center text-muted py-4"
                >
                  Nenhum formulário cadastrado.
                </div>
                <table
                  v-else
                  class="table table-hover mb-0"
                >
                  <thead>
                    <tr>
                      <th style="width: 40px" />
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th style="width: 80px">
                        Status
                      </th>
                      <th style="width: 190px">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <draggable
                    v-model="orderedFormDefinitions"
                    tag="tbody"
                    handle=".drag-handle"
                    ghost-class="drag-ghost"
                    :disabled="!$hasAbility('forms.admin')"
                    @end="onFormDefinitionDragEnd"
                  >
                    <tr
                      v-for="item in orderedFormDefinitions"
                      :key="item.id"
                      :class="{ 'table-active': (selectedFormDefinition && selectedFormDefinition.id === item.id) || editingFormDefinition.id === item.id }"
                    >
                      <td
                        class="align-middle text-center drag-handle"
                        style="cursor: grab"
                      >
                        <feather-icon
                          icon="MenuIcon"
                          size="14"
                          class="text-muted"
                        />
                      </td>
                      <td class="align-middle">
                        <span class="font-weight-bold">{{ item.name }}</span>
                      </td>
                      <td class="align-middle">
                        <small class="text-muted">{{ (item.description || '').substring(0, 60) }}</small>
                      </td>
                      <td class="align-middle">
                        <b-badge
                          :variant="item.status ? 'success' : 'secondary'"
                        >
                          {{ item.status ? "Publicado" : "Rascunho" }}
                        </b-badge>
                      </td>
                      <td
                        class="align-middle"
                        style="white-space: nowrap"
                      >
                        <b-button
                          v-if="$hasAbility('forms.admin')"
                          size="sm"
                          variant="primary"
                          class="mr-50"
                          title="Montar os campos deste formulário"
                          @click="goToFormBuilder(item)"
                        >
                          <feather-icon
                            icon="LayersIcon"
                            size="13"
                            class="mr-25"
                          />
                          Montar campos
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-secondary"
                          class="mr-50"
                          title="Associações — quem usa este formulário"
                          @click="selectFormDefinition(item)"
                        >
                          <feather-icon
                            icon="UsersIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-50"
                          title="Editar nome, descrição e status"
                          @click="editFormDefinition(item)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="13"
                          />
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          title="Excluir"
                          v-if="$hasAbility('forms.admin')"
                          @click="deleteFormDefinition(item)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="13"
                          />
                        </b-button>
                      </td>
                    </tr>
                  </draggable>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Seção C: associações — aparece ao clicar no botão de associações de um formulário -->
        <template v-if="selectedFormDefinition">
          <hr>

          <h6
            class="dc-section-title mb-1"
          >
            Associações — quem usa este formulário
          </h6>
          <div class="card">
            <div class="card-body">
              <div class="form-row align-items-end">
                <div class="form-group col-md-3">
                  <label>Disciplina:</label>
                  <v-select
                    v-model="editingFormAssignment.discipline_id"
                    :options="allDisciplines"
                    :reduce="(d) => d.id"
                    label="name"
                    placeholder="Todas"
                    class="vselect"
                  />
                </div>
                <div class="form-group col-md-3">
                  <label>Papel do envolvido:</label>
                  <v-select
                    v-model="editingFormAssignment.involved_role_id"
                    :options="involvedRolesAll"
                    :reduce="(r) => r.id"
                    label="label"
                    placeholder="Todos"
                    class="vselect"
                  />
                </div>
                <div class="form-group col-md-2">
                  <label>Papel de usuário:</label>
                  <v-select
                    v-model="editingFormAssignment.user_role_id"
                    :options="userRolesAll"
                    :reduce="(r) => r.id"
                    label="name"
                    placeholder="Todos"
                    class="vselect"
                  />
                </div>
                <div class="form-group col-md-2">
                  <label>Contexto:</label>
                  <select
                    v-model="editingFormAssignment.context"
                    class="form-control"
                  >
                    <option value="all">
                      Todos
                    </option>
                    <option value="web">
                      Web
                    </option>
                    <option value="app">
                      App
                    </option>
                  </select>
                </div>
                <div class="form-group col-md-1">
                  <b-form-checkbox
                    v-model="editingFormAssignment.status"
                    :value="1"
                    :unchecked-value="0"
                    switch
                  >
                    Ativo
                  </b-form-checkbox>
                </div>
                <div class="form-group col-md-1">
                  <b-button
                    variant="primary"
                    size="sm"
                    title="Adicionar associação"
                    v-if="$hasAbility('forms.admin')"
                    @click="saveFormAssignment"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>

              <div
                v-if="loadingFormAssignments"
                class="text-center py-3"
              >
                <b-spinner variant="primary" />
              </div>
              <div
                v-else-if="!formAssignments.length"
                class="text-muted small mt-2"
              >
                Nenhuma associação cadastrada — o formulário não aparece para ninguém ainda.
              </div>
              <table
                v-else
                class="table table-sm table-hover mb-0 mt-2"
              >
                <thead>
                  <tr>
                    <th>Disciplina</th>
                    <th>Papel do envolvido</th>
                    <th>Papel de usuário</th>
                    <th style="width: 80px">
                      Contexto
                    </th>
                    <th style="width: 80px">
                      Status
                    </th>
                    <th style="width: 60px">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="a in formAssignments"
                    :key="a.id"
                  >
                    <td>{{ disciplineNameById(a.discipline_id) }}</td>
                    <td>{{ involvedRoleLabelById(a.involved_role_id) }}</td>
                    <td>{{ userRoleNameById(a.user_role_id) }}</td>
                    <td>{{ contextLabel(a.context) }}</td>
                    <td>
                      <b-badge :variant="a.status ? 'success' : 'secondary'">
                        {{ a.status ? "Ativo" : "Inativo" }}
                      </b-badge>
                    </td>
                    <td>
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        title="Excluir"
                        v-if="$hasAbility('forms.admin')"
                        @click="deleteFormAssignment(a)"
                      >
                        <feather-icon
                          icon="TrashIcon"
                          size="13"
                        />
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </b-tab>

      <b-tab title="Regras de Negócio">
        <business-rules-tab :discipline-id="Number($route.params.disciplineId)" />
      </b-tab>

    </b-tabs>

    <!-- Modal: papéis de uma natureza (habilitar + configurar abas) -->
    <b-modal
      v-model="showRoleModal"
      :title="roleModalTitle"
      size="lg"
      centered
      hide-footer
    >
      <div v-if="loadingDisciplineRoles" class="text-muted small">
        <b-spinner small /> Carregando...
      </div>
      <template v-else>
        <p class="text-muted small">
          Marque os papéis desta natureza que a disciplina usa e, em cada um,
          quais abas ele exibe. Sem marcar abas, usa os padrões do papel.
        </p>
        <div v-if="!rolesForModal.length" class="text-muted small py-2">
          Nenhum papel disponível para esta natureza.
        </div>
        <div
          v-for="r in rolesForModal"
          :key="r.id"
          class="card mb-2"
        >
          <div class="card-body py-2">
            <div class="custom-control custom-switch">
              <input
                :id="`mrole-${r.id}`"
                v-model="r.enabled"
                type="checkbox"
                class="custom-control-input"
              >
              <label class="custom-control-label" :for="`mrole-${r.id}`">
                <strong>{{ r.label }}</strong>
              </label>
            </div>
            <div v-if="r.enabled" class="mt-2 pl-2">
              <small class="text-muted d-block mb-1">Abas exibidas:</small>
              <div
                v-for="p in allPanels"
                :key="p.key"
                class="custom-control custom-checkbox custom-control-inline"
              >
                <input
                  :id="`mdrp-${r.id}-${p.key}`"
                  v-model="r.panelKeys"
                  type="checkbox"
                  class="custom-control-input"
                  :value="p.key"
                >
                <label class="custom-control-label" :for="`mdrp-${r.id}-${p.key}`">
                  {{ p.label }}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mr-1"
            @click="showRoleModal = false"
          >
            Fechar
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="savingDisciplineRoles"
            v-if="$hasAbility('node.admin')"
            @click="saveDisciplineRoles"
          >
            <b-spinner v-if="savingDisciplineRoles" small class="mr-25" />
            Salvar papéis
          </button>
        </div>
      </template>
    </b-modal>

    <!-- Modal: condições de pulo de etapa -->
    <b-modal
      id="modal-step-skips"
      :title="
        skipModal.step
          ? `Condições de Pulo — ${skipModal.step.step_label}`
          : 'Condições de Pulo'
      "
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok="saveSkips"
    >
      <p class="text-muted small mb-1">
        A etapa será pulada automaticamente quando a ocorrência tiver um dos
        tipos ou motivos selecionados abaixo.
      </p>
      <b-form-group label="Tipos que pulam esta etapa">
        <v-select
          v-model="skipModal.selectedTypeIds"
          :options="dispatchTypes"
          :reduce="(t) => t.id"
          label="name"
          multiple
          placeholder="Selecione tipos..."
        />
      </b-form-group>
      <b-form-group label="Motivos que pulam esta etapa">
        <v-select
          v-model="skipModal.selectedReasonIds"
          :options="dispatchReasons"
          :reduce="(r) => r.id"
          label="name"
          multiple
          placeholder="Selecione motivos..."
        />
      </b-form-group>
    </b-modal>

    <!-- Modal: configurar predecessores -->
    <b-modal
      id="modal-predecessors"
      :title="
        configuring
          ? `Predecessores de ${configuring.code} — ${configuring.name}`
          : 'Predecessores'
      "
      ok-title="Salvar"
      cancel-title="Cancelar"
      size="md"
      @ok="savePredecessors"
    >
      <p class="text-muted small mb-2">
        Selecione quais posições precisam ser informadas
        <strong>antes</strong> de
        <b-badge variant="primary">{{
          configuring ? configuring.code : ""
        }}</b-badge>. <br>Deixe vazio para acesso livre.
      </p>
      <div class="form-group">
        <v-select
          v-model="selectedPredecessorIds"
          :options="availablePredecessors"
          :reduce="(t) => t.id"
          label="label"
          multiple
          placeholder="Nenhum predecessor — pode ser informado a qualquer momento"
          class="vselect"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BTabs,
  BTab,
  BButton,
  BModal,
  BSpinner,
  BBadge,
  BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import swal from 'sweetalert'
import BusinessRulesTab from '@/components/BusinessRulesTab.vue'

export default {
  components: {
    BTabs,
    BTab,
    BButton,
    BModal,
    BSpinner,
    BBadge,
    BFormCheckbox,
    vSelect,
    draggable,
    BusinessRulesTab,
  },

  data() {
    return {
      node: {},
      discipline: {},

      // Naturezas de envolvido habilitadas nesta disciplina (nó+disciplina)
      disciplineEntityTypes: [],
      loadingEntityTypes: false,
      savingEntityTypes: false,

      // Papéis (catálogo global) habilitados + abas por papel, nesta disciplina
      disciplineRoles: [],
      allPanels: [],
      loadingDisciplineRoles: false,
      savingDisciplineRoles: false,
      // Modal de papéis por natureza (engrenagem)
      showRoleModal: false,
      roleModalNature: null,

      positions: [],
      loadingPositions: false,

      editingPosition: {
        code: '',
        name: '',
        description: '',
        order: 0,
        scope: 'ocorrencia',
        free_access: false,
        display_kanban: false,
        allows_dispatch: true,
        is_default_position: false,
        releases_occurrence: false,
        status: 1,
      },

      // modal predecessores
      configuring: null,
      availablePredecessors: [],
      selectedPredecessorIds: [],

      // tipos de ocorrência
      dispatchTypes: [],
      loadingTypes: false,
      editingType: {
        name: '',
        description: '',
        status: 1,
      },

      // motivos
      dispatchReasons: [],
      loadingReasons: false,
      selectedTypeIdForReasons: null,
      editingReason: {
        dispatch_type_id: null,
        name: '',
        description: '',
        status: 1,
        interrogatory_id: null,
        interrogatory_required: false,
      },

      // interrogatórios
      interrogatories: [],
      loadingInterrogatories: false,
      editingInterrogatory: { name: '', description: '', status: 1 },
      selectedInterrogatoryId: null,
      questions: [],
      loadingQuestions: false,
      questionMode: 'add', // 'add' | 'edit'
      editingQuestion: {
        interrogatory_id: null,
        question: '',
        question_type: 2,
        required: false,
        next_question_id: null,
        order: 0,
        classification_id: null,
        status: 1,
      },
      editingAlternatives: [], // [{ label, next_question_id, classification_id, team_types: [] }]
      teamTypes: [], // tipos de equipe do tenant

      // desfechos
      outcomes: [],
      outcomeTypes: [],
      loadingOutcomes: false,
      editingOutcome: {
        name: '',
        description: '',
        types: [],
      },

      // classificações de risco
      classifications: [],
      loadingClassifications: false,
      editingClassification: {
        color_label: '',
        hex_color: '#FF0000',
        weight: 0,
        status: 1,
      },

      // fluxo de ocorrência
      flowSteps: [],
      skipModal: {
        step: null,
        selectedTypeIds: [],
        selectedReasonIds: [],
      },

      // intercorrências
      intercurrences: [],
      loadingIntercurrences: false,
      editingIntercurrence: { name: '', description: '', is_active: true },

      // decisões técnicas
      technicalDecisions: [],
      loadingTechnicalDecisions: false,
      editingTechnicalDecision: { name: '', status: 1 },

      // avaliação inicial
      assessmentQuestions: [],
      loadingAssessmentQuestions: false,
      editingAssessmentQuestion: {
        label: '', type: 'single_select', order: 0, is_active: true,
      },
      editingAssessmentQuestionOptions: [],
      inlineOptionInput: '',

      // problemas
      problems: [],
      loadingProblems: false,
      editingProblem: { label: '', order: 0, is_active: true },

      // ── formulários (form builder) ──────────────────────────────────────
      // Seção A: definições de formulário (sem order — tabela não possui essa coluna)
      formDefinitions: [],
      loadingFormDefinitions: false,
      editingFormDefinition: { name: '', description: '', status: true },

      // Seção B (edição de campos) foi movida para a tela dedicada do Form
      // Builder — aqui só resta o estado do formulário selecionado, usado
      // para exibir a Seção C (associações) abaixo da lista.
      selectedFormDefinition: null,

      // Seção C: associações (form_assignments)
      formAssignments: [],
      loadingFormAssignments: false,
      editingFormAssignment: {
        discipline_id: null,
        involved_role_id: null,
        user_role_id: null,
        context: 'all',
        status: 1,
      },
      allDisciplines: [], // catálogo de disciplinas do nó (picker "Disciplina" das associações)
      involvedRolesAll: [], // catálogo de papéis de envolvido (picker "Papel do envolvido")
      userRolesAll: [], // catálogo de papéis de usuário (picker "Papel de usuário")
    }
  },

  computed: {
    // Papéis da natureza selecionada na engrenagem (filtra o catálogo carregado).
    rolesForModal() {
      if (!this.roleModalNature) return []
      return this.disciplineRoles.filter(
        (r) => (r.entity_types || []).includes(this.roleModalNature.code),
      )
    },
    roleModalTitle() {
      return this.roleModalNature
        ? `Papéis — ${this.roleModalNature.label}`
        : 'Papéis'
    },
    positionsSorted() {
      return [...this.positions].sort(
        (a, b) => a.order - b.order || a.code.localeCompare(b.code),
      )
    },
    orderedPositions: {
      get() {
        return this.positionsSorted
      },
      set(newOrder) {
        newOrder.forEach((pos, idx) => {
          const found = this.positions.find(p => p.id === pos.id)
          if (found) found.order = idx
        })
      },
    },
    orderedQuestions: {
      get() {
        return [...this.questions].sort((a, b) => (a.order || 0) - (b.order || 0))
      },
      set(newOrder) {
        newOrder.forEach((q, idx) => {
          const found = this.questions.find(x => x.id === q.id)
          if (found) found.order = idx
        })
      },
    },
    orderedAssessmentQuestions: {
      get() {
        return [...this.assessmentQuestions].sort((a, b) => (a.order || 0) - (b.order || 0))
      },
      set(newOrder) {
        newOrder.forEach((q, idx) => {
          const found = this.assessmentQuestions.find(x => x.id === q.id)
          if (found) found.order = idx
        })
      },
    },
    orderedProblems: {
      get() {
        return [...this.problems].sort((a, b) => (a.order || 0) - (b.order || 0))
      },
      set(newOrder) {
        newOrder.forEach((p, idx) => {
          const found = this.problems.find(x => x.id === p.id)
          if (found) found.order = idx
        })
      },
    },
    orderedFormDefinitions: {
      get() {
        return [...this.formDefinitions].sort((a, b) => (a.order || 0) - (b.order || 0))
      },
      set(newOrder) {
        newOrder.forEach((item, idx) => {
          const found = this.formDefinitions.find(x => x.id === item.id)
          if (found) found.order = idx
        })
      },
    },
    tenantId() {
      return this.$store.state.user ? this.$store.state.user.tenant_id : null
    },
    filteredReasons() {
      if (!this.selectedTypeIdForReasons) return this.dispatchReasons
      return this.dispatchReasons.filter(
        r => r.dispatch_type_id === this.selectedTypeIdForReasons,
      )
    },
  },

  mounted() {
    this.loadAll()
  },

  methods: {
    typeNameById(typeId) {
      const t = this.dispatchTypes.find(t => t.id === typeId)
      return t ? t.name : '-'
    },

    goBack() {
      this.$router.push(`/nodes/admin/${this.$route.params.nodeId}`)
    },

    // ── drag & drop ─────────────────────────────────────────────────────────

    onDragEnd() {
      // Persiste a nova ordem de todos os itens de uma vez
      const updates = this.orderedPositions.map((pos, idx) => axios.post(`${this.$store.state.api}team_position_types/${pos.id}`, {
        ...pos,
        order: idx,
        _method: 'PUT',
      }))
      Promise.all(updates)
        .then(() => this.loadPositions())
        .catch(() => this.$message('Erro', 'Falha ao salvar a nova ordem.', 'error'))
    },

    // ── carregamentos ───────────────────────────────────────────────────────

    async loadAll() {
      const { nodeId, disciplineId } = this.$route.params

      const [nodeRes, discRes] = await Promise.all([
        axios.get(`${this.$store.state.api}nodes/${nodeId}?with[]=type`),
        axios.get(`${this.$store.state.api}discipline/${disciplineId}`),
      ])

      this.node = nodeRes.data.data[0]
      this.discipline = discRes.data.data[0]

      await Promise.all([this.loadPositions(), this.loadTypes()])
      this.loadAllReasons()
      this.loadInterrogatories()
      this.loadOutcomes()
      this.loadOutcomeTypes()
      this.loadClassifications()
      this.loadFlowSteps()
      this.loadIntercurrences()
      this.loadTechnicalDecisions()
      this.loadAssessmentQuestions()
      this.loadProblems()
      this.loadDisciplineEntityTypes()
      this.loadDisciplineRoles()
      this.loadFormDefinitions()
    },

    // ── naturezas de envolvido por disciplina ───────────────────────────────

    async loadDisciplineEntityTypes() {
      // Config é por (tenant, disciplina); o tenant vem do usuário autenticado no backend.
      const { disciplineId } = this.$route.params
      this.loadingEntityTypes = true
      try {
        const res = await axios.get(
          `${this.$store.state.api}discipline_entity_types`,
          { params: { discipline_id: Number(disciplineId) } },
        )
        this.disciplineEntityTypes = (res.data.data || []).map(t => ({ ...t, enabled: !!t.enabled }))
      } catch (e) {
        this.disciplineEntityTypes = []
      } finally {
        this.loadingEntityTypes = false
      }
    },

    async saveDisciplineEntityTypes() {
      const { disciplineId } = this.$route.params
      this.savingEntityTypes = true
      try {
        await axios.post(`${this.$store.state.api}discipline_entity_types`, {
          discipline_id: Number(disciplineId),
          entity_type_ids: this.disciplineEntityTypes.filter(t => t.enabled).map(t => t.id),
        })
        this.$message('Sucesso', 'Configuração salva.', 'success')
      } catch (e) {
        this.$message('Erro', 'Erro ao salvar configuração.', 'error')
      } finally {
        this.savingEntityTypes = false
      }
    },

    // ── papéis e suas abas (catálogo global, habilitado por disciplina) ──────

    openRoleModal(nature) {
      this.roleModalNature = nature
      this.showRoleModal = true
      if (!this.disciplineRoles.length) this.loadDisciplineRoles()
    },

    async loadDisciplineRoles() {
      const { disciplineId } = this.$route.params
      this.loadingDisciplineRoles = true
      try {
        const [panelsRes, rolesRes] = await Promise.all([
          axios.get(`${this.$store.state.api}involved_panels`),
          axios.get(`${this.$store.state.api}discipline_roles`, {
            params: { discipline_id: Number(disciplineId) },
          }),
        ])
        this.allPanels = panelsRes.data.data || []
        this.disciplineRoles = (rolesRes.data.data || []).map(r => ({
          ...r,
          enabled: !!r.enabled,
          // chaves dos painéis efetivos (override ou default), para os checkboxes
          panelKeys: (r.panels || []).map(p => p.key),
        }))
      } catch (e) {
        this.allPanels = []
        this.disciplineRoles = []
      } finally {
        this.loadingDisciplineRoles = false
      }
    },

    async saveDisciplineRoles() {
      const { disciplineId } = this.$route.params
      const disc = Number(disciplineId)
      this.savingDisciplineRoles = true
      try {
        const enabled = this.disciplineRoles.filter(r => r.enabled)

        // 1) papéis habilitados
        await axios.post(`${this.$store.state.api}discipline_roles`, {
          discipline_id: disc,
          role_ids: enabled.map(r => r.id),
        })

        // 2) abas de cada papel habilitado (na ordem do catálogo de painéis)
        await Promise.all(
          enabled.map(r => axios.post(`${this.$store.state.api}discipline_role_panels`, {
            discipline_id: disc,
            involved_role_id: r.id,
            panel_keys: this.allPanels
              .filter(p => r.panelKeys.includes(p.key))
              .map(p => p.key),
          })),
        )

        this.$message('Sucesso', 'Papéis salvos.', 'success')
        this.loadDisciplineRoles()
      } catch (e) {
        this.$message('Erro', 'Erro ao salvar papéis.', 'error')
      } finally {
        this.savingDisciplineRoles = false
      }
    },

    // ── tipos de ocorrência ─────────────────────────────────────────────────

    loadTypes() {
      const { disciplineId } = this.$route.params
      this.loadingTypes = true
      return axios
        .get(
          `${this.$store.state.api
          }dispatch_types?paginated=false&status=1&discipline_id=${disciplineId}`,
        )
        .then(r => {
          this.dispatchTypes = r.data.data || []
        })
        .finally(() => {
          this.loadingTypes = false
        })
    },

    cancelEditType() {
      this.editingType = { name: '', description: '', status: 1 }
    },

    editType(t) {
      this.editingType = { ...t }
    },

    saveType() {
      if (!this.editingType.name) {
        this.$message('Atenção', 'Nome é obrigatório.', 'warning')
        return
      }
      const disciplineId = Number(this.$route.params.disciplineId)
      const payload = {
        ...this.editingType,
        discipline_id: disciplineId,
        tenant_id: this.tenantId,
        description: this.editingType.description || null,
      }
      let api = `${this.$store.state.api}dispatch_types`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Tipo salvo com sucesso.', 'success')
          this.cancelEditType()
          this.loadTypes()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    async uploadTypePin(event) {
      const file = event.target.files[0]
      if (!file || !this.editingType.id) return
      const fd = new FormData()
      fd.append('pin', file)
      this.$loading(true)
      try {
        const r = await axios.post(
          `${this.$store.state.api}dispatch_types/${this.editingType.id}/pin`,
          fd,
        )
        this.$set(this.editingType, 'pin_icon', r.data.pin_url)
        const idx = this.dispatchTypes.findIndex(
          t => t.id === this.editingType.id,
        )
        if (idx !== -1) this.$set(this.dispatchTypes[idx], 'pin_icon', r.data.pin_url)
        this.$message('Sucesso', 'Pin atualizado.', 'success')
      } catch (e) {
        this.$message(null, 'Erro ao enviar pin.', 'error')
      } finally {
        this.$loading(false)
        event.target.value = ''
      }
    },

    async removeTypePin() {
      if (!this.editingType.id) return
      this.$loading(true)
      try {
        await axios.delete(
          `${this.$store.state.api}dispatch_types/${this.editingType.id}/pin`,
        )
        this.$set(this.editingType, 'pin_icon', null)
        const idx = this.dispatchTypes.findIndex(
          t => t.id === this.editingType.id,
        )
        if (idx !== -1) this.$set(this.dispatchTypes[idx], 'pin_icon', null)
        this.$message('Sucesso', 'Pin removido.', 'success')
      } catch (e) {
        this.$message(null, 'Erro ao remover pin.', 'error')
      } finally {
        this.$loading(false)
      }
    },

    async uploadDisciplinePin(event) {
      const file = event.target.files[0]
      if (!file || !this.discipline.id) return
      const fd = new FormData()
      fd.append('pin', file)
      this.$loading(true)
      try {
        const r = await axios.post(
          `${this.$store.state.api}discipline/${this.discipline.id}/pin`,
          fd,
        )
        this.$set(this.discipline, 'pin_icon', r.data.pin_url)
        this.$message('Sucesso', 'Pin da disciplina atualizado.', 'success')
      } catch (e) {
        this.$message(null, 'Erro ao enviar pin.', 'error')
      } finally {
        this.$loading(false)
        event.target.value = ''
      }
    },

    async removeDisciplinePin() {
      if (!this.discipline.id) return
      this.$loading(true)
      try {
        await axios.delete(
          `${this.$store.state.api}discipline/${this.discipline.id}/pin`,
        )
        this.$set(this.discipline, 'pin_icon', null)
        this.$message('Sucesso', 'Pin da disciplina removido.', 'success')
      } catch (e) {
        this.$message(null, 'Erro ao remover pin.', 'error')
      } finally {
        this.$loading(false)
      }
    },

    deleteType(t) {
      swal({
        title: `Excluir "${t.name}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}dispatch_types/${t.id}`)
          .then(() => {
            this.$message('Sucesso', 'Tipo excluído.', 'success')
            this.loadTypes()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── motivos ─────────────────────────────────────────────────────────────

    onReasonTypeChange(typeId) {
      this.cancelEditReason()
      this.editingReason.dispatch_type_id = typeId || null
    },

    loadAllReasons() {
      // Carrega todos os motivos dos tipos desta disciplina
      if (!this.dispatchTypes.length) return
      const typeIds = this.dispatchTypes.map(t => t.id)
      this.loadingReasons = true
      const requests = typeIds.map(id => axios.get(
        `${this.$store.state.api
        }dispatch_reasons?paginated=false&dispatch_type_id=${id}`,
      ))
      Promise.all(requests)
        .then(responses => {
          this.dispatchReasons = responses.flatMap(r => r.data.data || [])
        })
        .finally(() => {
          this.loadingReasons = false
        })
    },

    loadReasons(typeId) {
      // Recarrega motivos após salvar/excluir, mantendo o mesmo conjunto completo
      this.loadAllReasons()
    },

    cancelEditReason() {
      this.editingReason = {
        dispatch_type_id: this.selectedTypeIdForReasons,
        name: '',
        description: '',
        status: 1,
        interrogatory_id: null,
        interrogatory_required: false,
      }
    },

    editReason(r) {
      this.editingReason = { ...r }
    },

    saveReason() {
      if (!this.editingReason.name || !this.editingReason.dispatch_type_id) {
        this.$message('Atenção', 'Nome e tipo são obrigatórios.', 'warning')
        return
      }
      const payload = {
        ...this.editingReason,
        tenant_id: this.tenantId,
        description: this.editingReason.description || '',
      }
      let api = `${this.$store.state.api}dispatch_reasons`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Motivo salvo com sucesso.', 'success')
          this.cancelEditReason()
          this.loadReasons(this.selectedTypeIdForReasons)
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteReason(r) {
      swal({
        title: `Excluir "${r.name}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}dispatch_reasons/${r.id}`)
          .then(() => {
            this.$message('Sucesso', 'Motivo excluído.', 'success')
            this.loadReasons(this.selectedTypeIdForReasons)
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── desfechos ────────────────────────────────────────────────────────────

    loadOutcomes() {
      const { disciplineId } = this.$route.params
      this.loadingOutcomes = true
      return axios
        .get(
          `${this.$store.state.api
          }outcomes?paginated=false&discipline_id=${disciplineId}&with[]=types`,
        )
        .then(r => {
          this.outcomes = r.data.data || []
        })
        .finally(() => {
          this.loadingOutcomes = false
        })
    },

    loadOutcomeTypes() {
      return axios
        .get(`${this.$store.state.api}outcome_types?paginated=false`)
        .then(r => {
          this.outcomeTypes = r.data.data || []
        })
    },

    cancelEditOutcome() {
      this.editingOutcome = { name: '', description: '', types: [] }
    },

    editOutcome(oc) {
      this.editingOutcome = {
        ...oc,
        types: (oc.types || []).map(t => t.id),
      }
    },

    saveOutcome() {
      if (!this.editingOutcome.name) {
        this.$message('Atenção', 'Nome é obrigatório.', 'warning')
        return
      }
      const disciplineId = Number(this.$route.params.disciplineId)
      const payload = {
        name: this.editingOutcome.name,
        description: this.editingOutcome.description || null,
        discipline_id: disciplineId,
        types: this.editingOutcome.types || [],
      }
      let api = `${this.$store.state.api}outcomes`
      if (this.editingOutcome.id) {
        payload._method = 'PUT'
        api += `/${this.editingOutcome.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Desfecho salvo com sucesso.', 'success')
          this.cancelEditOutcome()
          this.loadOutcomes()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteOutcome(oc) {
      swal({
        title: `Excluir "${oc.name}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}outcomes/${oc.id}`)
          .then(() => {
            this.$message('Sucesso', 'Desfecho excluído.', 'success')
            this.loadOutcomes()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── classificações de risco ─────────────────────────────────────────────

    loadClassifications() {
      const { nodeId, disciplineId } = this.$route.params
      this.loadingClassifications = true
      return axios
        .get(
          `${this.$store.state.api
          }classifications?node_id=${nodeId}&discipline_id=${disciplineId}`,
        )
        .then(r => {
          this.classifications = r.data.data || []
        })
        .finally(() => {
          this.loadingClassifications = false
        })
    },

    cancelEditClassification() {
      this.editingClassification = {
        color_label: '',
        hex_color: '#FF0000',
        weight: 0,
        status: 1,
      }
    },

    editClassification(c) {
      this.editingClassification = { ...c }
    },

    saveClassification() {
      const c = this.editingClassification
      if (!c.color_label || !c.hex_color) {
        this.$message('Atenção', 'Rótulo e cor são obrigatórios.', 'warning')
        return
      }
      if (!/^#[0-9A-Fa-f]{6}$/.test(c.hex_color)) {
        this.$message('Atenção', 'Cor deve estar no formato #RRGGBB.', 'warning')
        return
      }
      const { nodeId, disciplineId } = this.$route.params
      const payload = {
        ...c,
        node_id: Number(nodeId),
        discipline_id: Number(disciplineId),
      }
      let api = `${this.$store.state.api}classifications`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Classificação salva com sucesso.', 'success')
          this.cancelEditClassification()
          this.loadClassifications()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteClassification(c) {
      swal({
        title: `Excluir "${c.name}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}classifications/${c.id}`)
          .then(() => {
            this.$message('Sucesso', 'Classificação excluída.', 'success')
            this.loadClassifications()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── interrogatórios ─────────────────────────────────────────────────────

    loadInterrogatories() {
      const { disciplineId } = this.$route.params
      this.loadingInterrogatories = true
      return axios
        .get(
          `${this.$store.state.api
          }interrogatories?paginated=false&discipline_id=${disciplineId}`,
        )
        .then(r => {
          this.interrogatories = r.data.data || []
        })
        .finally(() => {
          this.loadingInterrogatories = false
        })
    },

    cancelEditInterrogatory() {
      this.editingInterrogatory = { name: '', description: '', status: 1 }
    },

    editInterrogatory(item) {
      this.editingInterrogatory = { ...item }
      this.selectInterrogatory(item)
    },

    saveInterrogatory() {
      if (!this.editingInterrogatory.name) {
        this.$message('Atenção', 'Nome é obrigatório.', 'warning')
        return
      }
      const disciplineId = Number(this.$route.params.disciplineId)
      const payload = {
        ...this.editingInterrogatory,
        discipline_id: disciplineId,
        tenant_id: this.tenantId,
        description: this.editingInterrogatory.description || '',
      }
      let api = `${this.$store.state.api}interrogatories`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Interrogatório salvo.', 'success')
          this.cancelEditInterrogatory()
          this.loadInterrogatories()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteInterrogatory(item) {
      swal({
        title: `Excluir "${item.name}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}interrogatories/${item.id}`)
          .then(() => {
            this.$message('Sucesso', 'Interrogatório excluído.', 'success')
            if (this.selectedInterrogatoryId === item.id) {
              this.selectedInterrogatoryId = null
              this.questions = []
            }
            this.loadInterrogatories()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── perguntas ────────────────────────────────────────────────────────────

    selectInterrogatory(item) {
      this.selectedInterrogatoryId = item.id
      this.cancelEditQuestion()
      this.loadQuestions(item.id)
      this.loadTeamTypes()
    },

    loadQuestions(interrogatoryId) {
      this.loadingQuestions = true
      return axios
        .get(
          `${this.$store.state.api
          }interrogatory_questions?paginated=false&interrogatory_id=${interrogatoryId}&with[]=alternatives&with[]=alternatives.teamTypes&with[]=teamTypes`,
        )
        .then(r => {
          this.questions = r.data.data || []
        })
        .finally(() => {
          this.loadingQuestions = false
        })
    },

    cancelEditQuestion() {
      this.questionMode = 'add'
      this.editingQuestion = {
        interrogatory_id: this.selectedInterrogatoryId,
        question: '',
        question_type: 2,
        required: false,
        next_question_id: null,
        order: 0,
        classification_id: null,
        status: 1,
      }
      this.editingAlternatives = []
    },

    editQuestion(q) {
      this.questionMode = 'edit'
      this.editingQuestion = { ...q }
      this.editingAlternatives = (q.alternatives || []).map(a => ({
        id: a.id,
        label: a.label,
        next_question_id: a.next_question_id,
        classification_id: a.classification_id || null,
        team_types: (a.team_types || []).map(tt => ({ team_type_id: tt.id })),
      }))
      this.loadTeamTypes()
    },

    addAlternative() {
      this.editingAlternatives.push({
        label: '',
        next_question_id: null,
        classification_id: null,
        team_types: [],
      })
    },

    removeAlternative(index) {
      this.editingAlternatives.splice(index, 1)
    },

    saveAlternatives(questionId) {
      const { api } = this.$store.state
      const altsToSave = this.editingAlternatives.filter(a => a.label.trim())
      return axios
        .get(`${api}interrogatory_question_alternatives?paginated=false&interrogatory_question_id=${questionId}`)
        .then(r => {
          const existing = r.data.data || []
          return Promise.all(existing.map(a => axios.delete(`${api}interrogatory_question_alternatives/${a.id}`)))
        })
        .then(() => Promise.all(
          altsToSave.map((a, idx) => axios.post(`${api}interrogatory_question_alternatives`, {
            interrogatory_question_id: questionId,
            label: a.label.trim(),
            next_question_id: a.next_question_id || null,
            classification_id: a.classification_id || null,
            order: idx,
          })),
        ))
        .then(responses => Promise.all(
          responses.map((r, idx) => {
            const altData = altsToSave[idx]
            const altId = r.data.data ? r.data.data.id : (r.data.id || null)
            const types = (altData.team_types || []).filter(tt => tt.team_type_id)
            return this.syncAlternativeTeamTypes(altId, types)
          }),
        ))
    },

    syncAlternativeTeamTypes(alternativeId, types) {
      if (!alternativeId) return Promise.resolve()
      const { api } = this.$store.state
      return axios
        .post(`${api}interrogatory_question_alternatives/${alternativeId}/sync-team-types`, {
          team_types: types.map((tt, idx) => ({ team_type_id: tt.team_type_id, order: idx })),
        })
        .catch(() => Promise.resolve())
    },

    saveQuestion() {
      if (!this.editingQuestion.question) {
        this.$message('Atenção', 'O texto da pergunta é obrigatório.', 'warning')
        return
      }
      if (
        this.editingQuestion.question_type === 4
        && this.editingAlternatives.filter(a => a.label.trim()).length === 0
      ) {
        this.$message('Atenção', 'Adicione ao menos uma alternativa.', 'warning')
        return
      }
      const payload = {
        ...this.editingQuestion,
        interrogatory_id: this.selectedInterrogatoryId,
      }
      let apiUrl = `${this.$store.state.api}interrogatory_questions`
      if (payload.id) {
        payload._method = 'PUT'
        apiUrl += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(apiUrl, payload)
        .then(r => {
          const savedId = r.data.data ? r.data.data.id : r.data.id || payload.id
          const saves = []
          if (this.editingQuestion.question_type === 4) {
            saves.push(this.saveAlternatives(savedId))
          }
          return Promise.all(saves)
        })
        .then(() => {
          this.$message('Sucesso', 'Pergunta salva.', 'success')
          this.cancelEditQuestion()
          this.loadQuestions(this.selectedInterrogatoryId)
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteQuestion(q) {
      swal({
        title: 'Excluir esta pergunta?',
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}interrogatory_questions/${q.id}`)
          .then(() => {
            this.$message('Sucesso', 'Pergunta excluída.', 'success')
            this.loadQuestions(this.selectedInterrogatoryId)
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    questionNameById(qId) {
      const q = this.questions.find(q => q.id === qId)
      return q
        ? q.question.substring(0, 40) + (q.question.length > 40 ? '...' : '')
        : 'Encerrar'
    },

    loadTeamTypes() {
      axios
        .get(`${this.$store.state.api}team_types`)
        .then(r => {
          this.teamTypes = r.data.data || []
        })
        .catch(() => {})
    },

    onQuestionDragEnd() {
      const items = this.orderedQuestions.map((q, idx) => ({ id: q.id, order: idx }))
      axios
        .post(`${this.$store.state.api}interrogatory_questions/reorder`, { items })
        .then(() => this.loadQuestions(this.selectedInterrogatoryId))
        .catch(() => this.$message('Erro', 'Falha ao salvar a nova ordem.', 'error'))
    },

    onAssessmentQuestionDragEnd() {
      const items = this.orderedAssessmentQuestions.map((q, idx) => ({ id: q.id, order: idx }))
      axios
        .post(`${this.$store.state.api}initial_assessment_questions/reorder`, { items })
        .then(() => this.loadAssessmentQuestions())
        .catch(() => this.$message('Erro', 'Falha ao salvar a nova ordem.', 'error'))
    },

    onProblemDragEnd() {
      const items = this.orderedProblems.map((p, idx) => ({ id: p.id, order: idx }))
      axios
        .post(`${this.$store.state.api}problems/reorder`, { items })
        .then(() => this.loadProblems())
        .catch(() => this.$message('Erro', 'Falha ao salvar a nova ordem.', 'error'))
    },

    loadPositions() {
      this.loadingPositions = true
      const { disciplineId, nodeId } = this.$route.params
      // Aqui e a tela de configuracao: alem do que o no herda de cima,
      // o administrador precisa ver o que as bases abaixo cadastraram.
      return axios
        .get(
          `${this.$store.state.api
          }team_position_types/for-node?discipline_id=${disciplineId}&node_id=${nodeId}&with_descendants=1`,
        )
        .then(r => {
          this.positions = r.data.data || []
        })
        .finally(() => {
          this.loadingPositions = false
        })
    },

    // ── posições ────────────────────────────────────────────────────────────

    origemDaPosicao(pos) {
      if (pos.origin) return pos.origin

      return Number(pos.node_id) === Number(this.$route.params.nodeId) ? 'own' : 'inherited'
    },

    daPropriaNo(pos) {
      return this.origemDaPosicao(pos) === 'own'
    },

    textoDaOrigem(pos) {
      return {
        inherited: 'Herdada de um nó acima — edite no nó de origem',
        descendant: 'Cadastrada numa base abaixo — edite no nó de origem',
      }[this.origemDaPosicao(pos)] || 'Cadastrada neste nó'
    },

    cancelEdit() {
      this.editingPosition = {
        code: '',
        name: '',
        description: '',
        order: 0,
        scope: 'ocorrencia',
        free_access: false,
        display_kanban: false,
        allows_dispatch: true,
        is_default_position: false,
        releases_occurrence: false,
        status: 1,
      }
    },

    editPosition(pos) {
      this.editingPosition = { ...pos }
    },

    savePosition() {
      const self = this

      if (!this.editingPosition.code || !this.editingPosition.name) {
        this.$message('Atenção', 'Código e nome são obrigatórios.', 'warning')
        return
      }

      const { disciplineId, nodeId } = this.$route.params

      const payload = {
        ...this.editingPosition,
        discipline_id: Number(disciplineId),
        node_id: Number(nodeId),
      }

      let api = `${this.$store.state.api}team_position_types`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }

      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Posição salva com sucesso.', 'success')
          this.cancelEdit()
          this.loadPositions()
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response?.data, 'error')
        })
    },

    deletePosition(pos) {
      swal({
        title: `Excluir ${pos.code}?`,
        text: `A posição "${pos.code} — ${pos.name}" será removida.`,
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}team_position_types/${pos.id}`)
          .then(() => {
            this.$message('Sucesso', 'Posição excluída.', 'success')
            this.loadPositions()
            this.$loading(false)
          })
          .catch(e => {
            this.$loading(false)
            this.$message(null, e.response?.data, 'error')
          })
      })
    },

    toggleFreeAccess(pos) {
      const newVal = !pos.free_access
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}team_position_types/${pos.id}`, {
          ...pos,
          free_access: newVal,
          _method: 'PUT',
        })
        .then(() => {
          pos.free_access = newVal
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response?.data, 'error')
        })
    },

    toggleAllowsDispatch(pos) {
      const newVal = !pos.allows_dispatch
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}team_position_types/${pos.id}`, {
          ...pos,
          allows_dispatch: newVal,
          _method: 'PUT',
        })
        .then(() => {
          pos.allows_dispatch = newVal
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response?.data, 'error')
        })
    },

    toggleReleasesOccurrence(pos) {
      const newVal = !pos.releases_occurrence
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}team_position_types/${pos.id}`, {
          ...pos,
          releases_occurrence: newVal,
          _method: 'PUT',
        })
        .then(() => {
          pos.releases_occurrence = newVal
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response?.data, 'error')
        })
    },

    // ── predecessores ───────────────────────────────────────────────────────

    openPredecessors(pos) {
      this.configuring = pos
      this.selectedPredecessorIds = (pos.predecessors || []).map(p => p.id)
      this.availablePredecessors = this.positions
        .filter(p => p.id !== pos.id)
        .map(p => ({ ...p, label: `${p.code} — ${p.name}` }))
      this.$bvModal.show('modal-predecessors')
    },

    savePredecessors() {
      this.$loading(true)
      axios
        .post(
          `${this.$store.state.api
          }team_position_types/${this.configuring.id}/predecessors`,
          { predecessor_ids: this.selectedPredecessorIds },
        )
        .then(() => {
          this.$message('Sucesso', 'Regras de sucessão salvas.', 'success')
          this.loadPositions()
          this.$loading(false)
        })
        .catch(e => {
          this.$loading(false)
          this.$message(null, e.response?.data, 'error')
        })
    },

    // ── fluxo de ocorrência ─────────────────────────────────────────────────

    loadFlowSteps() {
      const { disciplineId } = this.$route.params
      axios
        .get(
          `${this.$store.state.api
          }discipline_flow_steps?discipline_id=${
            disciplineId}`,
        )
        .then(r => {
          this.flowSteps = r.data.data || []
        })
        .catch(() => {})
    },

    toggleFlowStep(step) {
      axios
        .patch(
          `${this.$store.state.api
          }discipline_flow_steps/${
            step.id
          }/toggle`,
        )
        .then(r => {
          this.$set(step, 'status', r.data.data.status)
        })
        .catch(() => this.$message('Erro', 'Não foi possível alterar o status.', 'error'))
    },

    openSkipModal(step) {
      const skips = step.skip_conditions || []
      this.skipModal.step = step
      this.skipModal.selectedTypeIds = skips
        .filter(s => s.dispatch_type_id)
        .map(s => s.dispatch_type_id)
      this.skipModal.selectedReasonIds = skips
        .filter(s => s.dispatch_reason_id)
        .map(s => s.dispatch_reason_id)
      this.$bvModal.show('modal-step-skips')
    },

    saveSkips() {
      const { step } = this.skipModal
      if (!step) return
      axios
        .post(
          `${this.$store.state.api}discipline_flow_steps/${step.id}/skips`,
          {
            dispatch_type_ids: this.skipModal.selectedTypeIds,
            dispatch_reason_ids: this.skipModal.selectedReasonIds,
          },
        )
        .then(r => {
          this.$set(step, 'skip_conditions', r.data.data.skip_conditions || [])
          this.$message('Sucesso', 'Condições de pulo salvas.', 'success')
        })
        .catch(() => this.$message('Erro', 'Não foi possível salvar.', 'error'))
    },

    // ── intercorrências ─────────────────────────────────────────────────────

    loadIntercurrences() {
      const { nodeId, disciplineId } = this.$route.params
      this.loadingIntercurrences = true
      return axios
        .get(
          `${this.$store.state.api
          }intercurrences?paginated=false&node_id=${nodeId}&discipline_id=${disciplineId}`,
        )
        .then(r => {
          this.intercurrences = r.data.data || []
        })
        .finally(() => {
          this.loadingIntercurrences = false
        })
    },

    cancelEditIntercurrence() {
      this.editingIntercurrence = { name: '', description: '', is_active: true }
    },

    editIntercurrence(item) {
      this.editingIntercurrence = { ...item }
    },

    saveIntercurrence() {
      if (!this.editingIntercurrence.name) {
        this.$message('Atenção', 'Nome é obrigatório.', 'warning')
        return
      }
      const { nodeId, disciplineId } = this.$route.params
      const payload = {
        ...this.editingIntercurrence,
        node_id: Number(nodeId),
        discipline_id: Number(disciplineId),
        description: this.editingIntercurrence.description || null,
      }
      let api = `${this.$store.state.api}intercurrences`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Intercorrência salva com sucesso.', 'success')
          this.cancelEditIntercurrence()
          this.loadIntercurrences()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteIntercurrence(item) {
      swal({
        title: `Excluir "${item.name}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}intercurrences/${item.id}`)
          .then(() => {
            this.$message('Sucesso', 'Intercorrência excluída.', 'success')
            this.loadIntercurrences()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── Decisões Técnicas ──

    loadTechnicalDecisions() {
      const { nodeId, disciplineId } = this.$route.params
      this.loadingTechnicalDecisions = true
      axios
        .get(
          `${this.$store.state.api
          }technical_decisions?paginated=false&discipline_id=${disciplineId}`,
        )
        .then(r => {
          this.technicalDecisions = r.data.data || []
        })
        .finally(() => {
          this.loadingTechnicalDecisions = false
        })
    },

    cancelEditTechnicalDecision() {
      this.editingTechnicalDecision = { name: '', status: 1 }
    },

    editTechnicalDecision(item) {
      this.editingTechnicalDecision = { ...item }
    },

    saveTechnicalDecision() {
      if (!this.editingTechnicalDecision.name) {
        this.$message('Atenção', 'Nome é obrigatório.', 'warning')
        return
      }
      const { disciplineId } = this.$route.params
      const payload = {
        ...this.editingTechnicalDecision,
        discipline_id: Number(disciplineId),
      }
      let api = `${this.$store.state.api}technical_decisions`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Decisão técnica salva com sucesso.', 'success')
          this.cancelEditTechnicalDecision()
          this.loadTechnicalDecisions()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    toggleTechnicalDecisionStatus(item) {
      const newStatus = item.status ? 0 : 1
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}technical_decisions/${item.id}`, {
          status: newStatus,
          _method: 'PUT',
        })
        .then(() => {
          this.$message('Sucesso', 'Status atualizado.', 'success')
          this.loadTechnicalDecisions()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteTechnicalDecision(item) {
      swal({
        title: `Excluir "${item.name}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}technical_decisions/${item.id}`)
          .then(() => {
            this.$message('Sucesso', 'Decisão técnica excluída.', 'success')
            this.loadTechnicalDecisions()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── Avaliação Inicial ────────────────────────────────────────────────────

    typeLabel(type) {
      return { single_select: 'Único', multiple_select: 'Múltiplo', text: 'Texto' }[type] || type
    },

    typeVariant(type) {
      return { single_select: 'light-primary', multiple_select: 'light-info', text: 'light-secondary' }[type] || 'secondary'
    },

    loadAssessmentQuestions() {
      const { nodeId, disciplineId } = this.$route.params
      this.loadingAssessmentQuestions = true
      return axios
        .get(
          `${this.$store.state.api
          }initial_assessment_questions?paginated=false&node_id=${nodeId}&discipline_id=${disciplineId}`,
        )
        .then(r => {
          this.assessmentQuestions = r.data.data || []
        })
        .finally(() => {
          this.loadingAssessmentQuestions = false
        })
    },

    cancelEditAssessmentQuestion() {
      this.editingAssessmentQuestion = {
        label: '', type: 'single_select', order: 0, is_active: true,
      }
      this.editingAssessmentQuestionOptions = []
      this.inlineOptionInput = ''
    },

    beginEditAssessmentQuestion(item) {
      this.editingAssessmentQuestion = { ...item }
      this.editingAssessmentQuestionOptions = (item.options || []).map(o => ({ ...o }))
      this.inlineOptionInput = ''
    },

    addInlineOption() {
      const label = this.inlineOptionInput.trim()
      if (!label) return
      const q = this.editingAssessmentQuestion
      if (q.id) {
        // Pergunta já salva: persiste imediatamente
        this.$loading(true)
        axios
          .post(`${this.$store.state.api}initial_assessment_questions/${q.id}/options`, {
            label,
            order: this.editingAssessmentQuestionOptions.length,
          })
          .then(r => {
            this.editingAssessmentQuestionOptions.push(r.data.data)
            this.inlineOptionInput = ''
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      } else {
        // Pergunta nova: guarda localmente até salvar
        this.editingAssessmentQuestionOptions.push({ label, _new: true })
        this.inlineOptionInput = ''
      }
    },

    removeInlineOption(opt, idx) {
      if (opt.id) {
        const q = this.editingAssessmentQuestion
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}initial_assessment_questions/${q.id}/options/${opt.id}`)
          .then(() => {
            this.editingAssessmentQuestionOptions.splice(idx, 1)
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      } else {
        this.editingAssessmentQuestionOptions.splice(idx, 1)
      }
    },

    saveAssessmentQuestion() {
      if (!this.editingAssessmentQuestion.label) {
        this.$message('Atenção', 'Rótulo é obrigatório.', 'warning')
        return
      }
      if (!this.editingAssessmentQuestion.type) {
        this.$message('Atenção', 'Tipo é obrigatório.', 'warning')
        return
      }
      const isSelect = ['single_select', 'multiple_select'].includes(this.editingAssessmentQuestion.type)
      if (isSelect && !this.editingAssessmentQuestion.id && !this.editingAssessmentQuestionOptions.length) {
        this.$message('Atenção', 'Adicione ao menos uma opção de resposta.', 'warning')
        return
      }
      const { nodeId, disciplineId } = this.$route.params
      const payload = {
        ...this.editingAssessmentQuestion,
        node_id: Number(nodeId),
        discipline_id: Number(disciplineId),
        order: this.editingAssessmentQuestion.order ?? 0,
      }
      let api = `${this.$store.state.api}initial_assessment_questions`
      const isEdit = !!payload.id
      if (isEdit) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(r => {
          const savedId = isEdit ? payload.id : (r.data.data?.id || r.data.id)
          // Salva as opções pendentes (novas, ainda sem ID)
          const pending = this.editingAssessmentQuestionOptions.filter(o => !o.id)
          return Promise.all(
            pending.map((o, idx) => axios.post(`${this.$store.state.api}initial_assessment_questions/${savedId}/options`, {
              label: o.label,
              order: (this.editingAssessmentQuestionOptions.filter(x => x.id).length) + idx,
            })),
          )
        })
        .then(() => {
          this.$message('Sucesso', 'Pergunta salva com sucesso.', 'success')
          this.cancelEditAssessmentQuestion()
          this.loadAssessmentQuestions()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteAssessmentQuestion(item) {
      swal({
        title: `Excluir "${item.label}"?`,
        text: 'As opções relacionadas também serão removidas.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}initial_assessment_questions/${item.id}`)
          .then(() => {
            this.$message('Sucesso', 'Pergunta excluída.', 'success')
            this.loadAssessmentQuestions()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    // ── Problemas ─────────────────────────────────────────────────────────────

    loadProblems() {
      const { nodeId, disciplineId } = this.$route.params
      this.loadingProblems = true
      return axios
        .get(
          `${this.$store.state.api
          }problems?paginated=false&node_id=${nodeId}&discipline_id=${disciplineId}`,
        )
        .then(r => {
          this.problems = r.data.data || []
        })
        .finally(() => {
          this.loadingProblems = false
        })
    },

    cancelEditProblem() {
      this.editingProblem = { label: '', order: 0, is_active: true }
    },

    beginEditProblem(item) {
      this.editingProblem = { ...item }
    },

    saveProblem() {
      if (!this.editingProblem.label) {
        this.$message('Atenção', 'Problema é obrigatório.', 'warning')
        return
      }
      const { nodeId, disciplineId } = this.$route.params
      const payload = {
        ...this.editingProblem,
        node_id: Number(nodeId),
        discipline_id: Number(disciplineId),
        order: this.editingProblem.order ?? 0,
      }
      let api = `${this.$store.state.api}problems`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Problema salvo com sucesso.', 'success')
          this.cancelEditProblem()
          this.loadProblems()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteProblem(item) {
      swal({
        title: `Excluir "${item.label}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}problems/${item.id}`)
          .then(() => {
            this.$message('Sucesso', 'Problema excluído.', 'success')
            this.loadProblems()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    skipSummary(step) {
      const skips = step.skip_conditions || []
      const types = skips
        .filter(s => s.dispatch_type_id)
        .map(s => {
          const t = this.dispatchTypes.find(x => x.id === s.dispatch_type_id)
          return t ? t.name : `Tipo #${s.dispatch_type_id}`
        })
      const reasons = skips
        .filter(s => s.dispatch_reason_id)
        .map(s => {
          const r = this.dispatchReasons.find(
            x => x.id === s.dispatch_reason_id,
          )
          return r ? r.name : `Motivo #${s.dispatch_reason_id}`
        })
      return [...types, ...reasons].join(', ')
    },

    // ── Formulários — Seção A: definições ───────────────────────────────────

    loadFormDefinitions() {
      this.loadingFormDefinitions = true
      return axios
        .get(`${this.$store.state.api}form_definitions?paginated=false`)
        .then(r => {
          this.formDefinitions = r.data.data || []
        })
        .finally(() => {
          this.loadingFormDefinitions = false
        })
    },

    onFormDefinitionDragEnd() {
      const items = this.orderedFormDefinitions.map((item, idx) => ({ id: item.id, order: idx }))
      axios
        .post(`${this.$store.state.api}form_definitions/reorder`, { items })
        .then(() => this.loadFormDefinitions())
        .catch(() => this.$message('Erro', 'Falha ao salvar a nova ordem.', 'error'))
    },

    cancelEditFormDefinition() {
      this.editingFormDefinition = { name: '', description: '', status: true }
    },

    editFormDefinition(item) {
      this.editingFormDefinition = { ...item }
    },

    saveFormDefinition() {
      if (!this.editingFormDefinition.name) {
        this.$message('Atenção', 'Nome é obrigatório.', 'warning')
        return
      }
      const payload = { ...this.editingFormDefinition }
      let api = `${this.$store.state.api}form_definitions`
      if (payload.id) {
        payload._method = 'PUT'
        api += `/${payload.id}`
      }
      this.$loading(true)
      axios
        .post(api, payload)
        .then(() => {
          this.$message('Sucesso', 'Formulário salvo com sucesso.', 'success')
          this.cancelEditFormDefinition()
          this.loadFormDefinitions()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteFormDefinition(item) {
      swal({
        title: `Excluir "${item.name}"?`,
        text: 'Os campos e associações deste formulário também serão removidos.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}form_definitions/${item.id}`)
          .then(() => {
            this.$message('Sucesso', 'Formulário excluído.', 'success')
            if (this.selectedFormDefinition && this.selectedFormDefinition.id === item.id) {
              this.selectedFormDefinition = null
              this.formAssignments = []
            }
            this.loadFormDefinitions()
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    goToFormBuilder(item) {
      this.$router.push({
        name: 'FormBuilder',
        params: { ...this.$route.params, formDefinitionId: item.id },
      })
    },

    // Mostra as Associações (Seção C) do formulário selecionado — a edição
    // de campos/lógica agora vive na tela dedicada do Form Builder.
    selectFormDefinition(item) {
      this.selectedFormDefinition = item
      this.cancelEditFormAssignment()
      this.loadFormAssignments(item.id)
      // Catálogos dos pickers de associação — carregados sob demanda (apenas 1x).
      if (!this.allDisciplines.length) this.loadAllDisciplines()
      if (!this.involvedRolesAll.length) this.loadInvolvedRolesAll()
      if (!this.userRolesAll.length) this.loadUserRolesAll()
    },

    // ── Formulários — Seção C: associações ──────────────────────────────────

    loadFormAssignments(formDefinitionId) {
      this.loadingFormAssignments = true
      return axios
        .get(`${this.$store.state.api}form_assignments?form_definition_id=${formDefinitionId}`)
        .then(r => {
          this.formAssignments = r.data.data || []
        })
        .finally(() => {
          this.loadingFormAssignments = false
        })
    },

    loadAllDisciplines() {
      return axios
        .get(`${this.$store.state.api}discipline?paginated=false&status=1`)
        .then(r => {
          this.allDisciplines = r.data.data || []
        })
        .catch(() => {})
    },

    loadInvolvedRolesAll() {
      return axios
        .get(`${this.$store.state.api}involved_roles`)
        .then(r => {
          this.involvedRolesAll = r.data.data || []
        })
        .catch(() => {})
    },

    loadUserRolesAll() {
      return axios
        .get(`${this.$store.state.api}roles?paginated=false&status=1`)
        .then(r => {
          this.userRolesAll = r.data.data || []
        })
        .catch(() => {})
    },

    cancelEditFormAssignment() {
      this.editingFormAssignment = {
        discipline_id: null,
        involved_role_id: null,
        user_role_id: null,
        context: 'all',
        status: 1,
      }
    },

    saveFormAssignment() {
      const payload = {
        ...this.editingFormAssignment,
        form_definition_id: this.selectedFormDefinition.id,
      }
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}form_assignments`, payload)
        .then(() => {
          this.$message('Sucesso', 'Associação criada com sucesso.', 'success')
          this.cancelEditFormAssignment()
          this.loadFormAssignments(this.selectedFormDefinition.id)
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    deleteFormAssignment(item) {
      swal({
        title: 'Excluir associação?',
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .delete(`${this.$store.state.api}form_assignments/${item.id}`)
          .then(() => {
            this.$message('Sucesso', 'Associação excluída.', 'success')
            this.loadFormAssignments(this.selectedFormDefinition.id)
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    disciplineNameById(id) {
      if (!id) return 'Todas'
      const d = this.allDisciplines.find(x => x.id === id)
      return d ? d.name : `#${id}`
    },

    involvedRoleLabelById(id) {
      if (!id) return 'Todos'
      const r = this.involvedRolesAll.find(x => x.id === id)
      return r ? r.label : `#${id}`
    },

    userRoleNameById(id) {
      if (!id) return 'Todos'
      const r = this.userRolesAll.find(x => x.id === id)
      return r ? r.name : `#${id}`
    },

    contextLabel(ctx) {
      const map = { web: 'Web', app: 'App', all: 'Todos' }
      return map[ctx] || ctx
    },
  },
}
</script>

<style scoped>
.card-body.p-0 {
  overflow-x: auto;
}

::v-deep .dc-tabs-nav {
  flex-wrap: wrap;
  row-gap: 2px;
}
::v-deep .dc-tabs-nav .nav-link {
  padding-left: 0.7rem;
  padding-right: 0.7rem;
  white-space: nowrap;
}

.dc-section-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e5873;
}
.dark-layout .dc-section-title {
  color: #b4b7bd;
}

.required {
  color: red;
}
.drag-handle {
  cursor: grab;
}
.drag-handle:active {
  cursor: grabbing;
}
.drag-ghost td {
  background: #f0f4ff !important;
  opacity: 0.7;
}
.pin-empty-preview {
  width: 48px;
  height: 48px;
  border: 2px dashed #ccc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #999;
  text-align: center;
}

/* ── Fluxo de Ocorrência ── */
.flow-step-fixed {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.88rem;
}
.flow-step-fixed--end {
  background: #f0faf4;
  border-color: #b8e6cc;
}
.flow-step-arrow {
  text-align: center;
  color: #adb5bd;
  font-size: 1.1rem;
  line-height: 1.6;
  user-select: none;
}
.flow-step-row {
  display: flex;
  align-items: center;
  padding: 7px 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 0;
  gap: 4px;
}
.flow-step-inactive {
  opacity: 0.55;
}
</style>
